import { difference, sortBy } from "lodash";
import { JSX, useContext, useEffect, useRef, useState } from "react";
import { CodeMapSet, CodeMapSetService, CodeMapType } from "@app/codemaps";
import { MemberSetModel } from "@app/members";
import {
  EditPerspective,
  ProjectExclusionType,
  ProjectOverrideMemberSetType,
  ProjectRunConfigurationStatus,
  TimeDistanceCalculatorType
} from "@app/projects/enumerators";
import EditAdequacyTemplateOptions from "@app/projects/network-assessment/EditAdequacyTemplateOptions";
import {
  EditTemplateOptionsHelper,
  InitialProjectState
} from "@app/projects/network-assessment/EditTemplateOptionsHelper";
import { preventDefaultMouseEvent } from "@app/shared/utility/preventDefaultMouseEvent";
import {
  AccessCriteriaGranularityEnum,
  TemplateCollectionMetadata,
  TemplateSpecialtyGroup
} from "@app/templates";
import BulkProjectEditChange from "@app/projects/edit/bulkProjectEditChange";
import { ColumnConfig, SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import ProviderSetService from "@app/provider-sets/providerSetService";
import { MemberTable } from "@app/projects/MemberTable";
import EditProjectOverrideMemberSet from "@app/projects/editProjectOverrideMemberSet";
import ProviderOptions from "@app/projects/network-assessment/EditProvidersOptions";
import { ProviderSet } from "@app/provider-sets";
import { EditDisplayOptions } from "@app/projects/network-assessment/EditDisplayOptions";
import Features from "@app/feature-flags/features";
import { BulkProjectEditChangeReason } from "@app/projects/edit/bulkProjectEditChangeReason";
import { timeDistanceCalculatorTypeDisplayMap } from "@app/projects/timeDistanceCalculatorTypeDisplayMap";
import getOptions from "@app/projects/create/data/options";
import { Fields } from "@app/projects/create/interfaces";
import { toast, TypeOptions } from "react-toastify";
import { Modal } from "@app/shared/Modals";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { NetworkTaxonomyData } from "@app/network-taxonomies/networkTaxonomy";
import NetworkTaxonomyContentPanel from "@app/network-taxonomies/NetworkTaxonomyContentPanel";
import { ResourceType } from "@app/resources";
import { NewActionMenu, NewActionMenuItem } from "@app/shared/action-menu";
import { ResourceFiltrationService } from "@app/projects/resource-filtration";
import NetworkTaxonomyService from "@app/network-taxonomies/networkTaxonomyService";
import { validateProviderSets } from "@app/projects/network-assessment/ValidateProviderSets";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { formatDateTime } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { sortByDate } from "@app/shared/sortByDate";
import Project from "@app/projects/project";
import ProjectService from "@app/projects/projectService";
import MultiselectDropdown from "@app/shared/checkbox-dropdown/MultiselectDropdown";
import SingleSelectDropdown from "@app/shared/checkbox-dropdown/SingleSelectDropdown";
import { DropdownItem } from "@app/shared/checkbox-dropdown/types";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { QESButton } from "@app/shared/qes-standards/Button";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import MemberSetService from "@app/members/memberSetService";
import TemplateService from "@app/templates/templateService";
import { ClientService } from "@app/client/clientService";
import { ResourceFilterSet } from "@app/resource-filters/filter-sets/resourceFilterSet";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import useIsNPMDashboardActive from "@app/network-portfolio-management/hooks/useIsNPMDashboardActive";
import { BulkProjectTypeEnum } from "@app/shared/enums/bulkProjectTypeEnum";

interface BulkEditModelTable {
  key: string;
  rowIsDisabled?: boolean;
  name: string;
  lastRunDate: string;
  lastRunStatus: string;
  lineOfBusiness: number;
  template: string;
  networkName: string;
  primaryMembershipName: string;
}

const PROJECT_TYPE_COUNTY = "County";
const PROJECT_TYPE_ZIP_CODE = "Zip Code";

const projectTypeOptions: DropdownItem<string>[] = [
  { key: PROJECT_TYPE_COUNTY, name: PROJECT_TYPE_COUNTY },
  { key: PROJECT_TYPE_ZIP_CODE, name: PROJECT_TYPE_ZIP_CODE },
];

enum ActiveEditModule {
  Projects = "Projects",
  Template = "Template",
  Members = "Members",
  Providers = "Providers",
  NetworkTaxonomy = "Network Taxonomy",
  Options = "Options",
  Summary = "Summary"
}

const columnConfig: ColumnConfig = {
  lastRunDate: {
    displayText: "Last Run",
    testId: "last-run-date",
  },
  lastRunStatus: {
    displayText: "Status of Last Run",
    testId: "last-run-status",
  },
  name: {
    displayText: "Project",
    testId: "name",
  },
  networkName: {
    displayText: "Network Name",
    testId: "network-name",
  },
  primaryMembershipName: {
    displayText: "Primary Membership Name",
    testId: "primary-membership-name",
  },
  rowIsDisabled: {
    displayText: "",
    hidden: true,
    testId: "",
  },
  template: {
    displayText: "Template",
    testId: "template",
  },
}

const BulkProjectEdit = (): JSX.Element => {
  const [loading, setLoading] = useState(true);
  const [loadingSelectedProjects, setLoadingSelectedProjects] = useState(true);
  const [editableProjects, setEditableProjects] = useState<BulkEditModelTable[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);
  const [volumeInitial, setVolumeInitial] = useState<BulkProjectEditChange>();
  const [volumeChange, setVolumeChange] = useState<BulkProjectEditChange>();
  const [project, setProject] = useState<Project>({
    accessCodeMapSetId: null,
    accuracySpecialtyDefinitionId: null,
    adequacyTemplateCollectionId: undefined,
    adequacyTemplateCorrelationId: undefined,
    editProjectOverrideMemberSets: [],
    excludedSpecialtyGroupCodes: [],
    memberSetId: null,
    prospectProviderSetId: null,
    telehealthSpecialtyGroupCodes: [],
    templateDescription: null,
    templateName: null,
    timeDistanceCalculatorType: undefined
  } as Project);
  const [projects, setProjects] = useState<Project[]>();
  const [filteredEditableProjects, setFilteredEditableProjects] = useState<BulkEditModelTable[]>([]);
  const [activeEditModule, setActiveEditModule] = useState(ActiveEditModule.Projects);
  const [nextEditModule, setNextEditModule] = useState<ActiveEditModule>();
  const [hasAccuracyOnlyOrZipProjects, setHasAccuracyOnlyOrZipProjects] = useState(false);
  const [templateResetDisabled, setTemplateResetDisabled] = useState(true);
  const [membersResetDisabled, setMembersResetDisabled] = useState(true);
  const [providersResetDisabled, setProvidersResetDisabled] = useState(true);
  const [networkTaxonomyResetDisabled, setNetworkTaxonomyResetDisabled] = useState(true);
  const [optionsResetDisabled, setOptionsResetDisabled] = useState(true);
  const [allAccessCodeMapSets, setAllAccessCodeMapSets] = useState<CodeMapSet[]>(null);
  const [allMemberPopulations, setAllMemberPopulations] = useState<MemberSetModel[]>(null);
  const [specialtyCodeMapSets, setSpecialtyCodeMapSets] = useState<CodeMapSet[]>(null);
  const [expandSpecialtyMileageCodeMapSets, setExpandSpecialtyMileageCodeMapSets] = useState<CodeMapSet[]>(null);
  const [templateCollections, setTemplateCollections] = useState<TemplateCollectionMetadata[]>(null);
  const [providerSets, setProviderSets] = useState<ProviderSet[]>(null);
  const [marketProviderSets, setMarketProviderSets] = useState<ProviderSet[]>(null);
  const [isNetworkTaxonomyValid, setIsNetworkTaxonomyValid] = useState<boolean>(true);
  const [hasMixedTaxonomy, setHasMixedTaxonomy] = useState<boolean>(false);
  const [sortOrder] = useState<Array<keyof TemplateSpecialtyGroup>>(["displayOrder"]);
  const [timeDistanceCalculatorTypes, setTimeDistanceCalculatorTypes] = useState<TimeDistanceCalculatorType[]>([]);
  const [projectAccessCodeMapSets, setProjectAccessCodeMapSets] = useState<CodeMapSet[]>([]);
  const [templateAccessGranularity, setTemplateAccessGranularity] = useState<AccessCriteriaGranularityEnum>(AccessCriteriaGranularityEnum.Unknown);
  const [sortedSpecialtyGroups, setSortedSpecialtyGroups] = useState<TemplateSpecialtyGroup[]>(null);
  const [projectMemberPopulations, setProjectMemberPopulations] = useState<MemberSetModel[]>(null);
  const [networkDisabled, setNetworkDisabled] = useState(false);
  const [closeModalWarningVisible, setCloseModalWarningVisible] = useState(false);
  const [hideCMSCredit, setHideCMSCredit] = useState(true);
  const [hideMotiveAppropriatenessScore, setHideMotiveAppropriatenessScore] = useState(true);
  const [hideEnterpriseComplianceDashboard, setHideEnterpriseComplianceDashboard] = useState(true);
  const [isValid, setIsValid] = useState(true);
  const [isProviderSetsValid, setIsProviderSetsValid] = useState(true);
  const [invalidNetworkTaxonomyProperties, setInvalidNetworkTaxonomyProperties] = useState<BulkProjectEditChangeReason[]>([]);
  const [templateChangeCount, setTemplateChangeCount] = useState(0);
  const [membersChangeCount, setMembersChangeCount] = useState(0);
  const [providersChangeCount, setProvidersChangeCount] = useState(0);
  const [networkTaxonomyChangeCount, setNetworkTaxonomyChangeCount] = useState(0);
  const [optionsChangeCount, setOptionsChangeCount] = useState(0);
  const [includedOptions, setIncludedOptions] = useState([]);
  const [excludedOptions, setExcludedOptions] = useState([]);
  const [saveChangesClicked, setSaveChangesClicked] = useState(false);
  const [options, setOptions] = useState<Fields<BulkProjectEditChange>[]>([]);
  const [projectSelectionChanged, setProjectSelectionChanged] = useState(false);
  const [warningForDisableDescription, setWarningForDisableDescription] = useState("");
  const [warningForDisableOption, setWarningForDisableOption] = useState<UpdateResourceKeys>();
  const [hideStrictTimeDistance, setHideStrictTimeDistance] = useState(false);
  const [hideUseInMarketAnalysis, setHideUseInMarketAnalysis] = useState(false);
  const [isZipProject, setIsZipProject] = useState(false);
  const [projectType, setProjectType] = useState<string>(PROJECT_TYPE_COUNTY);
  const [showNetworkSpecialtyCodeMap, setShowNetworkSpecialtyCodeMap] = useState(false);
  const [isDataShareEligible, setIsDataShareEligible] = useState(false);
  const [canApplyDashboardFilters, setCanApplyDashboardFilters] = useState(false);
  const [dashboardFilterResourceIds, setDashboardFilterResourceIds] = useState<string[]>([]);
  const [blendedOnly, setBlendedOnly] = useState(true);
  const [lineOfBusinessFilterOptions, setLineOfBusinessFilterOptions] = useState<DropdownItem<unknown>[]>([]);
  const [lineOfBusinessFilterOptionsDisabled, setLineOfBusinessFilterOptionsDisabled] = useState(false);
  const [filter, setFilter] = useState<DropdownItem<unknown>[]>([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const user = useContext(UserContext);
  const ecdCheckboxRef = useRef<HTMLInputElement>(null);
  const providerSetsValidatedByGetProjectsRef = useRef(false);
  // Whether each provider set field's own IDs across the selected projects were all available when loaded
  const providerSetFieldsValidOnLoadRef = useRef({ marketProviderSetId: true, networkProviderSetId: true, prospectProviderSetId: true });
  const editableProjectsRequestRef = useRef(0);
  const selectedProjectsRequestRef = useRef(0);
  const dashboardFilterRequestRef = useRef(0);
  const templateRequestRef = useRef(0);
  const { enabledFeatureFlags: featureFlags, featureFlagsLoading } = useContext(FeatureFlagContext);
  const zipBulkEditEnabled = featureFlags.has(Features.ZIP_BULK_EDIT);
  const zipProjectTypeSelected = zipBulkEditEnabled && projectType === PROJECT_TYPE_ZIP_CODE;
  const isNPMDashboardActive = useIsNPMDashboardActive();
  const templateHelper = new EditTemplateOptionsHelper();
  const projectService = new ProjectService();
  const resourceFiltrationService = new ResourceFiltrationService();
  const networkTaxonomyService = new NetworkTaxonomyService();
  const clientService = new ClientService();
  type UpdateResourceKeys = keyof BulkProjectEditChange;

  useEffect(() => {
    const requestId = ++dashboardFilterRequestRef.current;
    const isStale = () => requestId !== dashboardFilterRequestRef.current;
    if (editableProjects.length > 0) {
      const filters = window.localStorage.getItem(`${user.userId}project_filters`);
      const parsedFilters = JSON.parse(filters);
      try {
        const filters = getDashboardFilters();
        resourceFiltrationService.getFilteredProjectResources(filters).then((response) => {
          if (isStale()) return;
          if (!response) {
            setCanApplyDashboardFilters(false);
            return;
          }
          const filteredResourceIds = response.map((r) => r.resourceId.toString());
          const editableFilteredResourceIds =
            editableProjects.filter((e) => !e.rowIsDisabled && filteredResourceIds.includes(e.key)).map((e) => e.key);
          // intentionally replace any existing selections with these
          setCanApplyDashboardFilters(
            filters !== null
            && (parsedFilters?.filterGroups.length !== 0
              || parsedFilters?.pendingChanges.length !== 0)
            && editableFilteredResourceIds.length > 0
          );
          setDashboardFilterResourceIds(editableFilteredResourceIds);
        }).catch(() => {
          if (!isStale()) {
            setCanApplyDashboardFilters(false);
          }
        });
      } catch {
        if (!isStale()) {
          setCanApplyDashboardFilters(false);
        }
      }
    }
  }, [editableProjects]);

  useEffect(() => {
    const memberSetService = new MemberSetService();
    memberSetService.getActive(true, false).then((memberPopulations) => {
      setAllMemberPopulations(memberPopulations);
    });

    const codeMapSetService = new CodeMapSetService();
    let codeMapSets: CodeMapSet[] = [];
    codeMapSetService.get(CodeMapType.CountyClassMap).then((cms) => {
      codeMapSets = cms;
      codeMapSetService.get(CodeMapType.ZipCodeClassMap).then((cms) => {
        codeMapSets = [...codeMapSets, ...cms];
        setAllAccessCodeMapSets(codeMapSets);
      });
    });

    codeMapSetService.get(CodeMapType.SpecialtyCodeMap).then((cms) => {
      setSpecialtyCodeMapSets(cms);
    });

    codeMapSetService.get(CodeMapType.SpecialtyMileageExpansionCrosswalk).then((cms) => {
      setExpandSpecialtyMileageCodeMapSets(cms);
    });

    const templateService = new TemplateService();
    templateService.getUnarchivedTemplateCollections().then((c) => {
      setTemplateCollections(c);
    });

    const providerSetService = new ProviderSetService();
    providerSetService.getProviderSets().then((providerSets: ProviderSet[]) => {
      setProviderSets(providerSets);
    });

    providerSetService.getMarketProviderSets().then((providerSets) => {
      setMarketProviderSets(providerSets);
    });
  }, []);

  useEffect(() => {
    if (!!user) {
      clientService.getClient(user?.clientId).then((client) => {
        if (!!client) {
          setShowNetworkSpecialtyCodeMap(client?.networkBenchmarkingDataSource);
          setIsDataShareEligible(!!client?.isDataShareEligible);
        }
      });
    }
  }, [user]);

  useEffect(() => {
    setHideEnterpriseComplianceDashboard(!user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER));
    setHideStrictTimeDistance(featureFlags.has(Features.HIDE_STRICT_TIME_DISTANCE));
    setHideUseInMarketAnalysis(!user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER));
  }, [featureFlags, user]);

  useEffect(() => {
    if (featureFlagsLoading) return;
    if (!allAccessCodeMapSets || !specialtyCodeMapSets || !expandSpecialtyMileageCodeMapSets || !templateCollections || !allMemberPopulations || !providerSets || !marketProviderSets) return;

    getEditableProjects();

  }, [allAccessCodeMapSets, specialtyCodeMapSets, expandSpecialtyMileageCodeMapSets, templateCollections, allMemberPopulations, providerSets, marketProviderSets, featureFlagsLoading, zipBulkEditEnabled])

  useEffect(() => {
    if (!editableProjects || editableProjects.length === 0) return;
    if (editableProjects.find((p) => p.rowIsDisabled)) {
      setHasAccuracyOnlyOrZipProjects(true);
    }
  }, [editableProjects])

  useEffect(() => {
    if (volumeInitial === undefined) return;
    setVolumeChange(volumeInitial);
  }, [volumeInitial])

  useEffect(() => {
    if (volumeChange === undefined) return;
    setProject({
      /* oxlint-disable sort-keys */
      ...project,
      accessCodeMapSetId: volumeChange.accessCodeMapSetId,
      adequacyTemplateCollectionId: volumeChange.adequacyTemplateCollectionId,
      adequacyTemplateCorrelationId: volumeChange.adequacyTemplateCorrelationId,
      enablePatternOfCareProviders: volumeChange.enablePatternOfCareProviders,
      enableRegulatoryExceptionReports: volumeChange.enableRegulatoryExceptionReports,
      excludedSpecialtyGroupCodes: volumeChange.excludedSpecialtyGroupCodes,
      networkSpecialtyCodeMapSetId: volumeChange.networkSpecialtyCodeMapSetId,
      marketSpecialtyCodeMapSetId: volumeChange.marketSpecialtyCodeMapSetId,
      telehealthSpecialtyGroupCodes: volumeChange.telehealthSpecialtyGroupCodes,
      timeDistanceCalculatorType: volumeChange.timeDistanceCalculatorType,
      editProjectOverrideMemberSets: getEditProjectOverrideMemberSet(),
      memberSetId: volumeChange.memberSetId,
      memberSetAlias: volumeChange.memberSetAlias,
      memberSetId2: volumeChange.memberSetId2,
      memberSetAlias2: volumeChange.memberSetAlias2,
      networkProviderSetAlias: volumeChange.networkProviderSetAlias,
      networkProviderSetId: volumeChange.networkProviderSetId,
      networkTaxonomy: {
        ...project.networkTaxonomy,
        fileTypeId: volumeChange.fileTypeId,
        fileType: volumeChange.fileType,
        fileTypeReason: volumeChange.fileTypeReason,
        projectUtilizationCategoryId: volumeChange.projectUtilizationCategoryId,
        projectUtilizationCategory: volumeChange.projectUtilizationCategory,
        lineOfBusinessId: volumeChange.lineOfBusinessId,
        lineOfBusiness: volumeChange.lineOfBusiness,
        reason: volumeChange.lineOfBusinessReason,
        lineOfBusinessTypeId: volumeChange.lineOfBusinessTypeId,
        lineOfBusinessType: volumeChange.lineOfBusinessType,
        regulatorAssignmentClientId: volumeChange.regulatorAssignmentClientId,
        regulatorAssignmentClient: volumeChange.regulatorAssignmentClient,
      },
      prospectProviderSetAlias: volumeChange.prospectProviderSetAlias,
      prospectProviderSetId: volumeChange.prospectProviderSetId,
      marketProviderSetAlias: volumeChange.marketProviderSetAlias,
      marketProviderSetId: volumeChange.marketProviderSetId,
      nextClosestProviderSetAlias: volumeChange.nextClosestProviderSetAlias,
      useMarketProvidersForImpact: volumeChange.useMarketProvidersForImpact,
      hasZipSummaries: volumeChange.hasZipSummaries,
      includeGapAging: volumeChange.includeGapAging,
      autoArchiveUnarchiveSpecialtyExceptions: volumeChange.autoArchiveUnarchiveSpecialtyExceptions,
      excludeInVolumeActions: volumeChange.excludeInVolumeActions,
      showAdvancedComparison: volumeChange.showAdvancedComparison,
      showAffiliationsInsights: volumeChange.showAffiliationsInsights,
      affiliationsDisplayOnNetworkAccordion: volumeChange.affiliationsDisplayOnNetworkAccordion,
      showAttestation: volumeChange.showAttestation,
      showAverageDistance: volumeChange.showAverageDistance,
      showAverageTime: volumeChange.showAverageTime,
      showCMSCredit: volumeChange.showCMSCredit,
      showCareJourneyScores: volumeChange.showCareJourneyScores,
      showPgrConfidenceTier: volumeChange.showPgrConfidenceTier,
      showClosestProviderReport: volumeChange.showClosestProviderReport,
      showMaximumDistance: volumeChange.showMaximumDistance,
      showMaximumTime: volumeChange.showMaximumTime,
      showMipsScore: volumeChange.showMipsScore,
      showNextClosestNetworkProviders: volumeChange.showNextClosestNetworkProviders,
      showNppesDates: volumeChange.showNppesDates,
      showPatternOfCareProviders: volumeChange.showPatternOfCareProviders,
      showPciData: volumeChange.showPciData,
      showLocationConfidenceScores: volumeChange.showLocationConfidenceScores,
      showVaClaimsVolumeColumn: volumeChange.showVaClaimsVolumeColumn,
      showTricareClaimsVolumeColumn: volumeChange.showTricareClaimsVolumeColumn,
      showMotiveAppropriatenessScore: volumeChange.showMotiveAppropriatenessScore,
      strictTimeAndDistance: volumeChange.strictTimeAndDistance,
      networkExclusions: getNetworkExclusions(),
      impactExclusions: getImpactExclusions(),
      includeInEnterpriseComplianceDashboard: volumeChange.includeInEnterpriseComplianceDashboard,
      excludeProjectInSnowflakeDataShare: volumeChange.excludeProjectInSnowflakeDataShare,
      rppoNonContractedProvidersIncludedInHsd: volumeChange.rppoNonContractedProvidersIncludedInHsd,
      showPciDashboard: volumeChange.showPciDashboard,
      useProspectProvidersForMarketAnalysis: volumeChange.useProspectProvidersForMarketAnalysis,
      useNetworkProvidersForMarketAnalysis: volumeChange.useNetworkProvidersForMarketAnalysis,
      excludeAccuracyPractitionerAtLocationInspector: volumeChange.excludeAccuracyPractitionerAtLocationInspector,
      excludeProviderClaimsActivityInspector: volumeChange.excludeProviderClaimsActivityInspector,
      showNetworkIntegrityDashboard: volumeChange.showNetworkIntegrityDashboard,
      showNetworkIntegrityCriticalAlerts: volumeChange.showNetworkIntegrityCriticalAlerts,
      showNetworkIntegrityAttestationBasedAlerts: volumeChange.showNetworkIntegrityAttestationBasedAlerts,
      useSnowflakeProviderSetsForAnalysis: volumeChange.useSnowflakeProviderSetsForAnalysis,
      /* oxlint-enable sort-keys */
    })

    isResetDisabled();

    if (volumeChange.adequacyTemplateCollectionId === 0 ||
      volumeChange.adequacyTemplateCorrelationId === "" ||
      volumeChange.adequacyTemplateCorrelationId === "0" ||
      volumeChange.accessCodeMapSetId === null ||
      volumeChange.memberSetId === 0 ||
      volumeChange.networkProviderSetId === null ||
      (
        !isNetworkTaxonomyValid ||
        // has network taxonomy set up for all projects
        (volumeInitial.fileTypeId !== null && volumeChange.fileTypeId === null) ||
        (volumeInitial.projectUtilizationCategoryId !== null && volumeChange.projectUtilizationCategoryId === null) ||
        (volumeInitial.lineOfBusinessId !== null && volumeChange.lineOfBusinessId === null) ||
        // mixed network taxonomy and changes made to network taxonomy tab
        (hasMixedTaxonomy &&
          !(volumeChange.fileTypeId === -1 && volumeChange.lineOfBusinessId === -1 && volumeChange.projectUtilizationCategoryId === -1) &&
          !(volumeChange.fileTypeId !== -1 && volumeChange.lineOfBusinessId !== -1 && volumeChange.projectUtilizationCategoryId !== -1)) ||
        // network taxonomy was not orginally set up
        (!volumeInitial.fileTypeId &&
          !(!volumeChange.fileTypeId && !volumeChange.lineOfBusinessId && !volumeChange.projectUtilizationCategoryId) &&
          !(!!volumeChange.fileTypeId && !!volumeChange.lineOfBusinessId && !!volumeChange.projectUtilizationCategoryId))
      )
    ) {
      setIsValid(false);
    }
    else
      setIsValid(true);

    setLoadingSelectedProjects(false);
  }, [volumeChange, isNetworkTaxonomyValid, hasMixedTaxonomy])

  useEffect(() => {
    setOptions(getOptions<BulkProjectEditChange>({
      accuracyAndAdequacy: false,
      affiliationsDisplayOnNetworkAccordion: false,
      disableUnsupportedOptions: true,
      enableNetworkIntegrityOption: true,
      enablePgrConfidenceTierOption: true,
      hideClosestProviderReport: false,
      hideCMSApplicationCredit: hideCMSCredit,
      hideEnterpriseComplianceDashboard: hideEnterpriseComplianceDashboard,
      hideMotiveAppropriatenessScore: hideMotiveAppropriatenessScore,
      hidePciData: !project.adequacyTemplateCollectionId,
      hidePracAtLocCondifence: false,
      hideProviderClaimsActivity: false,
      hideRppoNonContractedProvidersIncludedInHsd: !featureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION),
      hideSnowflakeProjectRunProviderSets: !featureFlags.has(Features.SNOWFLAKE_PROJECT_RUN_PROVIDER_SETS),
      hideStrictTimeDistance: hideStrictTimeDistance,
      isDataShareEligible: isDataShareEligible,
      isZipProject: false,
      showAffiliationsInsights: false,
      showNcpInStateProvidersOnly: featureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS) && project.showNextClosestNetworkProviders,
      showNetworkIntegrityDashboardChecked: true,
      showPciDataChecked: true,
      showZipAnalysisNextClosestProviders: featureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS),
      useAccuracy: blendedOnly,
      userRoles: user.roles,
    })
      .reduce((acc, current) => acc = [...acc, ...current.fields], new Array<Fields<BulkProjectEditChange>>()));
  }, [hideCMSCredit, hideMotiveAppropriatenessScore, project, blendedOnly, isDataShareEligible])

  useEffect(() => {
    if (providerSetsValidatedByGetProjectsRef.current) {
      providerSetsValidatedByGetProjectsRef.current = false;
      return;
    }

    const availableProviderSets = [...(providerSets ?? []), ...(marketProviderSets ?? [])];
    const validOnLoad = providerSetFieldsValidOnLoadRef.current;
    // A mixed field (-1) is only as valid as the selected projects' own IDs for it were on load.
    // Any other field is validated against the value now chosen.
    const isFieldValid = (providerSetId: number, isValidOnLoad: boolean) =>
      providerSetId === -1 ? isValidOnLoad : validateProviderSets([providerSetId], availableProviderSets);

    setIsProviderSetsValid(
      isFieldValid(project.networkProviderSetId, validOnLoad.networkProviderSetId) &&
      isFieldValid(project.prospectProviderSetId, validOnLoad.prospectProviderSetId) &&
      isFieldValid(project.marketProviderSetId, validOnLoad.marketProviderSetId),
    );
  }, [project.networkProviderSetId, project.prospectProviderSetId, project.marketProviderSetId]);

  const getEditableProjects = (type: string = projectType) => {
    const requestId = ++editableProjectsRequestRef.current;
    projectService.getEditableProjects(zipBulkEditEnabled ? (type === PROJECT_TYPE_ZIP_CODE ? BulkProjectTypeEnum.Zip : BulkProjectTypeEnum.County) : undefined).then((projects) => {
      // Ignore stale responses when the project type changed again while this request was in flight
      if (requestId !== editableProjectsRequestRef.current) return;
      setEditableProjects(
        projects.map((p) => {
          /* oxlint-disable sort-keys */
          return {
            [wafflesKeyColumnName]: p.projectId.toString(),
            rowIsDisabled:
              zipBulkEditEnabled && type === PROJECT_TYPE_ZIP_CODE
                ? p.accuracyOnly || p.resourceType !== ResourceType.ZipAnalysisProject
                : p.accuracyOnly || p.resourceType == ResourceType.ZipAnalysisProject,
            name: p.name,
            lastRunDate: !!p.lastRunDate ? formatDateTime(p.lastRunDate) : "---",
            lastRunStatus: !!p.lastRunStatus ? getPrintableRunStatus(p.lastRunStatus) : "---",
            lineOfBusiness: p.lineOfBusinessId,
            template: !!p.template ? p.template : "---",
            networkName: !!p.networkId ? providerSets.find((ps) => ps.providerSetId === p.networkId)?.resource?.name ?? "---" : "---",
            primaryMembershipName: !!p.primaryMembershipId ? allMemberPopulations.find((m) => m.memberSetId === p.primaryMembershipId)?.resource?.name ?? "---" : "---",
          }
          /* oxlint-enable sort-keys */
        }));
      setLoading(false);
    }).catch(() => {
      if (requestId !== editableProjectsRequestRef.current) return;
      setEditableProjects([]);
      setLoading(false);
      errorToast("Failed to load projects. Please try again.");
    });
  };

  const getNetworkExclusions = () => {
    const networkExclusions: ProjectExclusionType[] = [];
    if (volumeChange.excludeDeactivatedFacilitiesNetwork)
      networkExclusions.push(ProjectExclusionType.DeactivatedFacility);
    if (volumeChange.excludeDeactivatedProvidersNetwork)
      networkExclusions.push(ProjectExclusionType.DeactivatedProvider);
    if (volumeChange.excludeInvalidNppesNpiFacilitiesNetwork)
      networkExclusions.push(ProjectExclusionType.InvalidNppesNpiFacility);
    if (volumeChange.excludeInvalidNppesNpiProvidersNetwork)
      networkExclusions.push(ProjectExclusionType.InvalidNppesNpiProvider);
    if (volumeChange.excludeMissingNpiFacilitiesNetwork)
      networkExclusions.push(ProjectExclusionType.MissingNpiFacility);
    if (volumeChange.excludeMissingNpiProvidersNetwork)
      networkExclusions.push(ProjectExclusionType.MissingNpiProvider);
    if (volumeChange.excludeMedicareOptOutProvidersNetwork)
      networkExclusions.push(ProjectExclusionType.MedicareOptOut);
    if (volumeChange.excludeOIGExcludedProvidersNetwork)
      networkExclusions.push(ProjectExclusionType.OIGExclusion);
    if (volumeChange.excludePrecludedProvidersNetwork)
      networkExclusions.push(ProjectExclusionType.Precluded);
    return networkExclusions;
  }

  const getImpactExclusions = () => {
    const impactExclusions: ProjectExclusionType[] = [];
    if (volumeChange.excludeDeactivatedFacilitiesImpact)
      impactExclusions.push(ProjectExclusionType.DeactivatedFacility);
    if (volumeChange.excludeDeactivatedProvidersImpact)
      impactExclusions.push(ProjectExclusionType.DeactivatedProvider);
    if (volumeChange.excludeInvalidNppesNpiFacilitiesImpact)
      impactExclusions.push(ProjectExclusionType.InvalidNppesNpiFacility);
    if (volumeChange.excludeInvalidNppesNpiProvidersImpact)
      impactExclusions.push(ProjectExclusionType.InvalidNppesNpiProvider);
    if (volumeChange.excludeMissingNpiFacilitiesImpact)
      impactExclusions.push(ProjectExclusionType.MissingNpiFacility);
    if (volumeChange.excludeMissingNpiProvidersImpact)
      impactExclusions.push(ProjectExclusionType.MissingNpiProvider);
    if (volumeChange.excludeMedicareOptOutProvidersImpact)
      impactExclusions.push(ProjectExclusionType.MedicareOptOut);
    if (volumeChange.excludeOIGExcludedProvidersImpact)
      impactExclusions.push(ProjectExclusionType.OIGExclusion);
    if (volumeChange.excludePrecludedProvidersImpact)
      impactExclusions.push(ProjectExclusionType.Precluded);

    return impactExclusions;
  }

  const renderPane = (tab: ActiveEditModule) => {
    return (
      <li className="nav-item">
        <a
          className={activeEditModule === tab ? "nav-link active" : "nav-link"}
          href="#"
          role="tab"
          style={{ pointerEvents: selectedProjects.length === 0 || !isValid || saveChangesClicked ? 'none' : 'auto' }}
          onClick={(e: React.MouseEvent) => preventDefaultMouseEvent(e, () => handlePaneClick(tab))}
        >
          {tab}
        </a>
      </li>
    );
  }

  const handlePaneClick = (tab: ActiveEditModule) => {
    const selectionChanged = projectSelectionChanged;
    if (activeEditModule === ActiveEditModule.Projects && projectSelectionChanged) {
      setLoadingSelectedProjects(true);
      setVolumeChange(undefined);
      getProjects(selectedProjects.map((p) => parseInt(p)))
      setProjectSelectionChanged(false);
      setActiveEditModule(tab);
    }
    else if (activeEditModule === ActiveEditModule.Providers && !providersResetDisabled) {
      setNextEditModule(tab);
      setCloseModalWarningVisible(true);
    }
    else {
      setActiveEditModule(tab);
    }

    if (selectionChanged && (tab === ActiveEditModule.Projects || activeEditModule === ActiveEditModule.Projects)) {
      setIsProviderSetsValid(true);
    }

    if (tab === ActiveEditModule.Summary && volumeChange !== undefined) {
      getSummaryInformation();
    }
  }

  const getSummaryInformation = () => {
    let templateChangeCount = 0;
    const changeReasons: BulkProjectEditChangeReason[] = [];
    if (volumeInitial.adequacyTemplateCollectionId !== volumeChange.adequacyTemplateCollectionId) {
      templateChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.AdequacyTemplateCollectionId);
    }
    if (volumeInitial.adequacyTemplateCorrelationId !== volumeChange.adequacyTemplateCorrelationId) {
      templateChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.AdequacyTemplateCorrelationId);
    }
    if (volumeInitial.timeDistanceCalculatorType !== volumeChange.timeDistanceCalculatorType) {
      templateChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.TimeDistanceCalculatorType);
    }
    if (volumeInitial.accessCodeMapSetId !== volumeChange.accessCodeMapSetId) {
      templateChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.AccessCodeMapSetId);
    }
    if (showNetworkSpecialtyCodeMap && volumeInitial.networkSpecialtyCodeMapSetId !== volumeChange.networkSpecialtyCodeMapSetId) {
      templateChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.NetworkSpecialtyCodeMapSetId);
    }
    if (volumeInitial.marketSpecialtyCodeMapSetId !== volumeChange.marketSpecialtyCodeMapSetId) {
      templateChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MarketSpecialtyCodeMapSetId);
    }
    let includedSpecialtyGroups: string[] = []
    let excludedSpecialtyGroups: string[] = []
    let includedTelehealth: string[] = []
    let excludedTelehealth: string[] = []
    if (volumeInitial.adequacyTemplateCorrelationId === volumeChange.adequacyTemplateCorrelationId) {
      includedSpecialtyGroups = difference(volumeInitial.excludedSpecialtyGroupCodes, volumeChange.excludedSpecialtyGroupCodes);
      if (includedSpecialtyGroups.length > 0) {
        templateChangeCount += includedSpecialtyGroups.length;
        changeReasons.push(BulkProjectEditChangeReason.IncludedSpecialtyGroupCodes);
      }
      const initialDifference = difference(volumeInitial.excludedSpecialtyGroupCodes, volumeInitial.disagreedExcludedSpecialtyGroupCodes);
      const changeDifference = difference(volumeChange.excludedSpecialtyGroupCodes, volumeChange.disagreedExcludedSpecialtyGroupCodes);
      excludedSpecialtyGroups = difference(changeDifference, initialDifference);
      if (excludedSpecialtyGroups.length > 0) {
        templateChangeCount += excludedSpecialtyGroups.length;
        changeReasons.push(BulkProjectEditChangeReason.ExcludedSpecialtyGroupCodes);
      }
      excludedTelehealth = difference(volumeInitial.telehealthSpecialtyGroupCodes, volumeChange.telehealthSpecialtyGroupCodes);
      excludedTelehealth = excludedTelehealth.concat(difference(difference(volumeInitial.disagreedTelehealthSpecialtyGroupCodes, volumeChange.telehealthSpecialtyGroupCodes), volumeChange.disagreedTelehealthSpecialtyGroupCodes));
      if (excludedTelehealth.length > 0) {
        templateChangeCount += excludedTelehealth.length;
        changeReasons.push(BulkProjectEditChangeReason.ExcludedTelehealthSpecailtyGroupCodes);
      }
      includedTelehealth = difference(volumeChange.telehealthSpecialtyGroupCodes, volumeInitial.telehealthSpecialtyGroupCodes);
      if (includedTelehealth.length > 0) {
        templateChangeCount += includedTelehealth.length;
        changeReasons.push(BulkProjectEditChangeReason.IncludedTelehealthSpecailtyGroupCodes);
      }
    } else { //template changed all specialty groups and telehealth will be updated

      excludedSpecialtyGroups = volumeChange.excludedSpecialtyGroupCodes;

      templateChangeCount += excludedSpecialtyGroups.length;
      changeReasons.push(BulkProjectEditChangeReason.ExcludedSpecialtyGroupCodes);

      includedTelehealth = volumeChange.telehealthSpecialtyGroupCodes;

      templateChangeCount += includedTelehealth.length;
      changeReasons.push(BulkProjectEditChangeReason.IncludedTelehealthSpecailtyGroupCodes);


    }

    let membersChangeCount = 0;
    if (volumeInitial.memberSetId !== volumeChange.memberSetId) {
      membersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MemberSetId);
    }
    if (volumeInitial.memberSetAlias !== volumeChange.memberSetAlias) {
      membersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MemberSetAlias);
    }
    if (volumeInitial.memberSetId2 !== volumeChange.memberSetId2) {
      membersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MemberSetId2);
    }
    if (volumeInitial.memberSetAlias2 !== volumeChange.memberSetAlias2) {
      membersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MemberSetAlias2);
    }

    let providersChangeCount = 0;
    if (volumeInitial.networkProviderSetId !== volumeChange.networkProviderSetId) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.NetworkProviderSetId);
    }
    if (volumeInitial.networkProviderSetAlias !== volumeChange.networkProviderSetAlias) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.NetworkProviderSetAlias);
    }
    if (volumeInitial.prospectProviderSetId !== volumeChange.prospectProviderSetId) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.ProspectProviderSetId);
    }
    if (volumeInitial.prospectProviderSetAlias !== volumeChange.prospectProviderSetAlias) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.ProspectProviderSetAlias);
    }
    if (volumeInitial.marketProviderSetId !== volumeChange.marketProviderSetId) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MarketProviderSetId);
    }
    if (volumeInitial.marketProviderSetAlias !== volumeChange.marketProviderSetAlias) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.MarketProviderSetAlias);
    }
    if (volumeInitial.nextClosestProviderSetAlias !== volumeChange.nextClosestProviderSetAlias) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.NextClosestProviderSetAlias);
    }
    if (volumeInitial.useProspectProvidersForMarketAnalysis !== volumeChange.useProspectProvidersForMarketAnalysis) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.UseProspectProvidersForMarketAnalysis);
    }
    if (volumeInitial.useNetworkProvidersForMarketAnalysis !== volumeChange.useNetworkProvidersForMarketAnalysis) {
      providersChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.UseNetworkProvidersForMarketAnalysis);
    }

    let networkTaxonomyChangeCount = 0;
    if (volumeInitial.fileTypeId !== volumeChange.fileTypeId) {
      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.FileTypeId);
    }
    if (((!volumeInitial.fileTypeReason && !!volumeChange.fileTypeReason) ||
      (!!volumeInitial.fileTypeReason && !volumeChange.fileTypeReason)) ||
      (!!volumeInitial.fileTypeReason && !!volumeChange.fileTypeReason
        && volumeInitial.fileTypeReason !== volumeChange.fileTypeReason)) {

      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.FileTypeReason);
    }
    if (volumeInitial.regulatorAssignmentClientId !== volumeChange.regulatorAssignmentClientId) {
      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.RegulatorAssignmentClientId);
    }
    if (volumeInitial.projectUtilizationCategoryId !== volumeChange.projectUtilizationCategoryId) {
      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.ProjectUtilizationCategoryId);
    }
    if (volumeInitial.lineOfBusinessId !== volumeChange.lineOfBusinessId) {
      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.LineOfBusinessId);
    }
    if (((!volumeInitial.lineOfBusinessTypeId && !!volumeChange.lineOfBusinessTypeId) ||
      (!!volumeInitial.lineOfBusinessTypeId && !volumeChange.lineOfBusinessTypeId)) ||
      (!!volumeInitial.lineOfBusinessTypeId && !!volumeChange.lineOfBusinessTypeId
        && volumeInitial.lineOfBusinessTypeId !== volumeChange.lineOfBusinessTypeId)) {

      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.LineOfBusinessTypeId);
    }
    if (((!volumeInitial.lineOfBusinessReason && !!volumeChange.lineOfBusinessReason) ||
      (!!volumeInitial.lineOfBusinessReason && !volumeChange.lineOfBusinessReason)) ||
      (!!volumeInitial.lineOfBusinessReason && !!volumeChange.lineOfBusinessReason
        && volumeInitial.lineOfBusinessReason !== volumeChange.lineOfBusinessReason)) {

      networkTaxonomyChangeCount++;
      changeReasons.push(BulkProjectEditChangeReason.Reason);
    }

    const includedOptions: string[] = [];
    const excludedOptions: string[] = [];
    options.forEach(option => {
      if (volumeInitial[option.key] !== volumeChange[option.key]) {
        volumeChange[option.key] ? includedOptions.push(option.title) : excludedOptions.push(option.title);
        changeReasons.push(Object.entries(BulkProjectEditChangeReason).find(([, value]) => value === option.key)[1]);
      }
    })
    if (hideCMSCredit && volumeInitial.showCMSCredit !== volumeChange.showCMSCredit) {
      excludedOptions.push("Apply CMS Application Credit");
      changeReasons.push(BulkProjectEditChangeReason.ShowCMSCredit);
    }
    if (hideMotiveAppropriatenessScore && volumeInitial.showMotiveAppropriatenessScore !== volumeChange.showMotiveAppropriatenessScore) {
      changeReasons.push(BulkProjectEditChangeReason.ShowMotiveAppropriatenessScore);
    }

    setTemplateChangeCount(templateChangeCount);
    setMembersChangeCount(membersChangeCount);
    setProvidersChangeCount(providersChangeCount);
    setNetworkTaxonomyChangeCount(networkTaxonomyChangeCount);
    setOptionsChangeCount(includedOptions.length + excludedOptions.length);
    setIncludedOptions(includedOptions);
    setExcludedOptions(excludedOptions);
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      projectChangeReasons: changeReasons,
      specialtyGroupCodesToExclude: excludedSpecialtyGroups,
      specialtyGroupCodesToInclude: includedSpecialtyGroups,
      telehealthSpecialtyGroupCodesToExclude: excludedTelehealth,
      telehealthSpecialtyGroupCodesToInclude: includedTelehealth,
    }));
  }

  const handleContinueClick = () => {
    setCloseModalWarningVisible(false);
    setActiveEditModule(nextEditModule);
  }

  const getPrintableRunStatus = (status: ProjectRunConfigurationStatus) => {
    switch (status) {
      case ProjectRunConfigurationStatus.QueuedProviderChanges:
      case ProjectRunConfigurationStatus.ProcessingProviderChanges:
        return "Processing Provider Changes";
      case ProjectRunConfigurationStatus.QueuedSpecialtyGroupExceptionAnalysis:
      case ProjectRunConfigurationStatus.ProcessingSpecialtyGroupExceptionAnalysis:
        return "Recalculating results for Specialty Group Exception Change";
      case ProjectRunConfigurationStatus.QueuedProspectMarketNcp:
      case ProjectRunConfigurationStatus.ProcessingProspectMarketNcp:
        return "Processing Next Closest Providers";
      default:
        return `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
    }
  }

  const handleCheckboxChange = (selectedKeys: string[]) => {
    setSelectedProjects(selectedKeys);
    setProjectSelectionChanged(true);
  };

  const getProjects = (projectIds: number[]) => {
    const requestId = ++selectedProjectsRequestRef.current;
    const isStale = () => requestId !== selectedProjectsRequestRef.current;

    projectService.getByIds(projectIds).then(async (projects) => {
      // Ignore stale responses when a newer load started or the project type changed
      if (isStale()) return;
      setProjects(projects);

      const templateCollectionIds = [... new Set(projects.map((p) => p.adequacyTemplateCollectionId))];
      const templateCorrelationsIds = templateCollectionIds.length === 1 ? [... new Set(projects.map((p) => p.adequacyTemplateCorrelationId))] : [];
      let timeDistanceTypes: TimeDistanceCalculatorType[] = [];
      let accessCodeMapSetIds: number[] = [];
      let networkSpecialtyCodeMapSetIds: number[] = [];
      let marketSpecialtyCodeMapSetIds: number[] = [];
      let excludedSpecialtyGroupCodes: string[] = [];
      let disagreedExcludedSpecialtyGroupCodes: string[] = [];
      let telehealthSpecialtyGroupCodes: string[] = [];
      let disagreedTelehealthSpecialtyGroupCodes: string[] = [];

      // All projects are using the same template
      if (templateCorrelationsIds.length === 1) {
        timeDistanceTypes = [... new Set(projects.map((p) => p.timeDistanceCalculatorType))];
        accessCodeMapSetIds = [... new Set(projects.map((p) => p.accessCodeMapSetId))];
        networkSpecialtyCodeMapSetIds = [... new Set(projects.map((p) => p.networkSpecialtyCodeMapSetId))];
        marketSpecialtyCodeMapSetIds = [... new Set(projects.map((p) => p.marketSpecialtyCodeMapSetId))];

        excludedSpecialtyGroupCodes = [... new Set(projects.flatMap((p) => p.excludedSpecialtyGroupCodes))]
        disagreedExcludedSpecialtyGroupCodes = excludedSpecialtyGroupCodes.filter(ex => !projects.every(p => p.excludedSpecialtyGroupCodes.includes(ex)));

        telehealthSpecialtyGroupCodes = [... new Set(projects.flatMap((p) => p.telehealthSpecialtyGroupCodes))];
        disagreedTelehealthSpecialtyGroupCodes = telehealthSpecialtyGroupCodes.filter(t => !projects.every(p => p.telehealthSpecialtyGroupCodes.includes(t)));
        telehealthSpecialtyGroupCodes = difference(telehealthSpecialtyGroupCodes, disagreedTelehealthSpecialtyGroupCodes);
      }

      const memberSetIds = [... new Set(projects.map((p) => p.memberSetId))];
      const memberSetIds2 = [... new Set(projects.map((p) => p.memberSetId2))];
      const memberSetAliases = [... new Set(projects.map((p) => p.memberSetAlias))];
      const memberSetAliases2 = [... new Set(projects.map((p) => p.memberSetAlias2))];

      const networkProviderSetIds = [... new Set(projects.map((p) => p.networkProviderSetId))];
      const prospectProviderSetIds = [... new Set(projects.map((p) => p.prospectProviderSetId))];
      const marketProviderSetIds = [... new Set(projects.map((p) => p.marketProviderSetId))];
      const useMarketProvidersForImpact = [... new Set(projects.map((p) => p.useMarketProvidersForImpact))];
      const networkProviderSetAliases = [... new Set(projects.map((p) => p.networkProviderSetAlias))];
      const prospectProviderSetAliases = [... new Set(projects.map((p) => p.prospectProviderSetAlias))];
      const marketProviderSetAliases = [... new Set(projects.map((p) => p.marketProviderSetAlias))];
      const nextClosestProviderSetAliases = [... new Set(projects.map((p) => p.nextClosestProviderSetAlias))];

      const fileTypeIds = [... new Set(projects.map((p) => p.networkTaxonomy?.fileTypeId))];
      const fileTypeReasons = [... new Set(projects.map((p) => p.networkTaxonomy?.fileTypeReason))];
      const regulatorAssignmentClientIds = [... new Set(projects.map((p) => p.networkTaxonomy?.regulatorAssignmentClientId))];
      const projectUtilizationCategoryIds = [... new Set(projects.map((p) => p.networkTaxonomy?.projectUtilizationCategoryId))];
      const lineOfBusinessIds = [... new Set(projects.map((p) => p.networkTaxonomy?.lineOfBusinessId))];
      const lineOfBusinessTypeIds = [... new Set(projects.map((p) => p.networkTaxonomy?.lineOfBusinessTypeId))];
      const reasons = [... new Set(projects.map((p) => p.networkTaxonomy?.reason))];

      const strictTimeAndDistances = [... new Set(projects.map((p) => p.strictTimeAndDistance))];
      const autoArchiveUnarchiveSpecialtyExceptions = [... new Set(projects.map((p) => p.autoArchiveUnarchiveSpecialtyExceptions))];
      const showCMSCredits = [... new Set(projects.map((p) => p.showCMSCredit))];
      const includeGapAgings = [... new Set(projects.map((p) => p.includeGapAging))];
      const showAttestations = [... new Set(projects.map((p) => p.showAttestation))];
      const showNppesDates = [... new Set(projects.map((p) => p.showNppesDates))];
      const showAdvancedComparisons = [... new Set(projects.map((p) => p.showAdvancedComparison))];
      const showAverageDistances = [... new Set(projects.map((p) => p.showAverageDistance))];
      const showMaximumDistances = [... new Set(projects.map((p) => p.showMaximumDistance))];
      const showAverageTimes = [... new Set(projects.map((p) => p.showAverageTime))];
      const showMaximumTimes = [... new Set(projects.map((p) => p.showMaximumTime))];
      const hasZipSummaries = [... new Set(projects.map((p) => p.hasZipSummaries))];
      const hasEnablePatternOfCareProviders = [... new Set(projects.map((p) => p.enablePatternOfCareProviders))];
      const hasEnableRegulatoryExceptionReport = [... new Set(projects.map((p) => p.enableRegulatoryExceptionReports))];
      const showNextClosestNetworkProviders = [... new Set(projects.map((p) => p.showNextClosestNetworkProviders))];
      const showPatternOfCareProviders = [... new Set(projects.map((p) => p.showPatternOfCareProviders))];
      const showClosestProviderReports = [... new Set(projects.map((p) => p.showClosestProviderReport))];
      const showMipsScores = [... new Set(projects.map((p) => p.showMipsScore))];
      const showPciDatas = [... new Set(projects.map((p) => p.showPciData))];
      const showLocationConfidenceScores = [... new Set(projects.map((p) => p.showLocationConfidenceScores))];
      const showVaClaimsVolumeColumn = [... new Set(projects.map((p) => p.showVaClaimsVolumeColumn))];
      const showTricareClaimsVolumeColumn = [... new Set(projects.map((p) => p.showTricareClaimsVolumeColumn))];
      const excludeInVolumeActions = [... new Set(projects.map(p => p.excludeInVolumeActions))];
      const includeInEnterpriseComplianceDashboard = [... new Set(projects.map(p => p.includeInEnterpriseComplianceDashboard))];
      const excludeProjectInSnowflakeDataShares = [... new Set(projects.map(p => p.excludeProjectInSnowflakeDataShare))];
      const rppoNonContractedProvidersIncludedInHsds = [... new Set(projects.map(p => p.rppoNonContractedProvidersIncludedInHsd))];
      const showPciDashboards = [... new Set(projects.map((p) => p.showPciDashboard))];
      const showMotiveAppropriatenessScores = [... new Set(projects.map((p) => p.showMotiveAppropriatenessScore))];
      const excludeAccuracyPractitionerAtLocationInspector = [... new Set(projects.map((p) => p.excludeAccuracyPractitionerAtLocationInspector))];
      const excludeProviderClaimsActivityInspector = [... new Set(projects.map((p) => p.excludeProviderClaimsActivityInspector))];
      const showCareJourneyScores = [... new Set(projects.map((p) => p.showCareJourneyScores))];
      const showPgrConfidenceTier = [... new Set(projects.map((p) => p.showPgrConfidenceTier))];
      const showAffiliationsInsights = [... new Set(projects.map((p) => p.showAffiliationsInsights))];
      const showNetworkIntegrityDashboards = [... new Set(projects.map((p) => p.showNetworkIntegrityDashboard))];
      const useSnowflakeProviderSetsForAnalysis = [... new Set(projects.map((p) => p.useSnowflakeProviderSetsForAnalysis))];
      const showNetworkIntegrityCriticalAlerts = [... new Set(projects.map((p) => p.showNetworkIntegrityCriticalAlerts))];
      const showNetworkIntegrityAttestationBasedAlerts = [... new Set(projects.map((p) => p.showNetworkIntegrityAttestationBasedAlerts))];
      const affiliationsDisplayOnNetworkAccordion = [... new Set(projects.map((p) => p.affiliationsDisplayOnNetworkAccordion))]

      let showMotiveAppropriatenessScore = false;

      if (templateCorrelationsIds.length === 1) {
        const templateInfo = await getTemplateInfo(templateCollectionIds[0], templateCorrelationsIds[0], allAccessCodeMapSets, allMemberPopulations, templateCollections);
        if (isStale()) return;
        applyTemplateInfo(templateInfo);
        showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId !== null;
      }
      else {
        const templateService = new TemplateService();

        // if any one of the projects does not have a Motive crosswalk, the Motive project option should not be shown
        for (const project of projects) {
          const templateInfo = await templateService.getLatestTemplateByCorrelationId(project.adequacyTemplateCollectionId, project.adequacyTemplateCorrelationId, false);
          if (isStale()) return;
          if (templateInfo.motiveCrosswalkCodeMapSetId === undefined || templateInfo.motiveCrosswalkCodeMapSetId === null) {
            showMotiveAppropriatenessScore = false;
            break;
          } else {
            showMotiveAppropriatenessScore = true;
          }
        }
      }

      setHideMotiveAppropriatenessScore(!showMotiveAppropriatenessScore);

      const networkExclusions = projects.map((p) => p.networkExclusions);
      const impactExclusions = projects.map((p) => p.impactExclusions);

      const useProspectProvidersForMarketAnalysis = [... new Set(projects.map((p) => p.useProspectProvidersForMarketAnalysis))];
      const useNetworkProvidersForMarketAnalysis = [... new Set(projects.map((p) => p.useNetworkProvidersForMarketAnalysis))];

      let hasCmsApplicationCredits: boolean;
      let isZipTemplate = false;
      if (templateCorrelationsIds.length === 1) {
        const templateInfo = await getTemplateInfo(templateCollectionIds[0], templateCorrelationsIds[0], allAccessCodeMapSets, allMemberPopulations, templateCollections);
        if (isStale()) return;
        applyTemplateInfo(templateInfo);
        hasCmsApplicationCredits = templateInfo.specialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);
        isZipTemplate = templateCollections.find((tc) => tc.templateCollectionId === project.adequacyTemplateCollectionId)?.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
      }
      else {
        const templateService = new TemplateService();
        projects.forEach(async project => {
          const specialtyGroups = await templateService.getLatestTemplateSpecialtyGroupsByCorrelationId(project.adequacyTemplateCollectionId, project.adequacyTemplateCorrelationId);
          hasCmsApplicationCredits = specialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);
          if (!hasCmsApplicationCredits) return;
        })

        setTimeDistanceCalculatorTypes([]);
        setProjectAccessCodeMapSets([]);
        setTemplateAccessGranularity(AccessCriteriaGranularityEnum.Unknown);
        setSortedSpecialtyGroups(null);
        setProjectMemberPopulations(allMemberPopulations);
      }

      setIsZipProject(zipProjectTypeSelected || isZipTemplate);
      setHideCMSCredit(!hasCmsApplicationCredits);
      setNetworkDisabled(projects.filter(p => p.specialtyGroupNetworkProviderSetCodeMapSetId)?.length > 0 ? true : false);
      setBlendedOnly(projects.every((project) => { return project.accuracyTemplateCollectionId }));

      const hasMixedTaxonomy = projects.some(p => !!p.networkTaxonomy) && projects.some(p => !p.networkTaxonomy);
      setHasMixedTaxonomy(hasMixedTaxonomy);

      // Only skip the effect when this load changes the IDs it depends on; otherwise it never runs
      // and the flag would swallow the next user edit.
      const idOrMixed = (ids: number[]) => ids.length === 1 ? ids[0] ?? 0 : -1;
      providerSetsValidatedByGetProjectsRef.current =
        idOrMixed(networkProviderSetIds) !== project.networkProviderSetId ||
        idOrMixed(prospectProviderSetIds) !== project.prospectProviderSetId ||
        idOrMixed(marketProviderSetIds) !== project.marketProviderSetId;

      const availableProviderSets = [...(providerSets ?? []), ...(marketProviderSets ?? [])];
      const isFieldValidOnLoad = (field: "networkProviderSetId" | "prospectProviderSetId" | "marketProviderSetId") =>
        validateProviderSets(projects.map(p => p[field]), availableProviderSets);
      const fieldsValidOnLoad = {
        marketProviderSetId: isFieldValidOnLoad("marketProviderSetId"),
        networkProviderSetId: isFieldValidOnLoad("networkProviderSetId"),
        prospectProviderSetId: isFieldValidOnLoad("prospectProviderSetId"),
      };
      providerSetFieldsValidOnLoadRef.current = fieldsValidOnLoad;
      setIsProviderSetsValid(Object.values(fieldsValidOnLoad).every(Boolean));

      const hasZipSummariesValue = hasZipSummaries.length === 1 ? hasZipSummaries[0] ?? false : undefined;
      const showNextClosestNetworkProvidersValue = showNextClosestNetworkProviders.length === 1 ? showNextClosestNetworkProviders[0] ?? false : undefined;
      const showPatternOfCareProvidersValue = showPatternOfCareProviders.length === 1 ? showPatternOfCareProviders[0] ?? false : undefined;

      setVolumeInitial({
        /* oxlint-disable sort-keys */
        ...volumeInitial,
        selectedProjects: projectIds,
        accessCodeMapSetId: accessCodeMapSetIds.length === 1 ? accessCodeMapSetIds[0] ?? 0 : -1,
        adequacyTemplateCollectionId: templateCollectionIds.length === 1 ? templateCollectionIds[0] ?? 0 : -1,
        adequacyTemplateCorrelationId: templateCorrelationsIds.length === 1 ? templateCorrelationsIds[0] ?? "0" : "-1",
        excludedSpecialtyGroupCodes: excludedSpecialtyGroupCodes,
        disagreedExcludedSpecialtyGroupCodes: disagreedExcludedSpecialtyGroupCodes,
        networkSpecialtyCodeMapSetId: networkSpecialtyCodeMapSetIds.length === 1 ? networkSpecialtyCodeMapSetIds[0] ?? 0 : -1,
        marketSpecialtyCodeMapSetId: marketSpecialtyCodeMapSetIds.length === 1 ? marketSpecialtyCodeMapSetIds[0] ?? 0 : -1,
        telehealthSpecialtyGroupCodes: telehealthSpecialtyGroupCodes,
        disagreedTelehealthSpecialtyGroupCodes: disagreedTelehealthSpecialtyGroupCodes,
        timeDistanceCalculatorType: timeDistanceTypes.length === 1 ? timeDistanceTypes[0] ?? TimeDistanceCalculatorType.Unknown : undefined,
        memberSetId: memberSetIds.length === 1 ? memberSetIds[0] ?? 0 : -1,
        memberSetAlias: memberSetAliases.length === 1 ? memberSetAliases[0] ?? "" : "-1",
        memberSetId2: memberSetIds2.length === 1 ? memberSetIds2[0] ?? 0 : -1,
        memberSetAlias2: memberSetAliases2.length === 1 ? memberSetAliases2[0] ?? "" : "-1",
        networkProviderSetAlias: networkProviderSetAliases.length === 1 ? networkProviderSetAliases[0] ?? "" : undefined,
        networkProviderSetId: networkProviderSetIds.length === 1 ? networkProviderSetIds[0] ?? 0 : -1,
        prospectProviderSetAlias: prospectProviderSetAliases.length === 1 ? prospectProviderSetAliases[0] ?? "" : undefined,
        prospectProviderSetId: prospectProviderSetIds.length === 1 ? prospectProviderSetIds[0] ?? 0 : -1,
        marketProviderSetAlias: marketProviderSetAliases.length === 1 ? marketProviderSetAliases[0] ?? "" : undefined,
        marketProviderSetId: marketProviderSetIds.length === 1 ? marketProviderSetIds[0] ?? 0 : -1,
        useMarketProvidersForImpact: useMarketProvidersForImpact.length === 1 ? useMarketProvidersForImpact[0] ?? false : undefined,
        nextClosestProviderSetAlias: nextClosestProviderSetAliases.length === 1 ? nextClosestProviderSetAliases[0] ?? "" : undefined,
        // if all fileTypes agree on null and have a fileTypeReason set up, then it is Other Data (-2)
        fileTypeId: fileTypeIds.length === 1 && !hasMixedTaxonomy ? fileTypeIds[0] ?? (!!projects[0].networkTaxonomy?.fileTypeReason ? -2 : null) : -1,
        fileType: fileTypeIds.length === 1 && !hasMixedTaxonomy ? projects[0].networkTaxonomy?.fileType : null,
        fileTypeReason: fileTypeReasons.length === 1 && !hasMixedTaxonomy ? fileTypeReasons[0] : "-1",
        projectUtilizationCategoryId: projectUtilizationCategoryIds.length === 1 && !hasMixedTaxonomy ? projectUtilizationCategoryIds[0] : -1,
        projectUtilizationCategory: projectUtilizationCategoryIds.length === 1 && !hasMixedTaxonomy ? projects[0].networkTaxonomy?.projectUtilizationCategory : null,
        lineOfBusinessId: lineOfBusinessIds.length === 1 && !hasMixedTaxonomy ? lineOfBusinessIds[0] : -1,
        lineOfBusiness: lineOfBusinessIds.length === 1 && !hasMixedTaxonomy ? projects[0].networkTaxonomy?.lineOfBusiness : null,
        lineOfBusinessTypeId: lineOfBusinessTypeIds.length === 1 && !hasMixedTaxonomy ? lineOfBusinessTypeIds[0] : -1,
        lineOfBusinessType: lineOfBusinessTypeIds.length === 1 && !hasMixedTaxonomy ? projects[0].networkTaxonomy?.lineOfBusinessType : null,
        lineOfBusinessReason: reasons.length === 1 && !hasMixedTaxonomy ? reasons[0] : "-1",
        regulatorAssignmentClient: regulatorAssignmentClientIds.length === 1 && !hasMixedTaxonomy ? projects[0].networkTaxonomy?.regulatorAssignmentClient : null,
        regulatorAssignmentClientId: regulatorAssignmentClientIds.length === 1 && !hasMixedTaxonomy ? projects[0].networkTaxonomy?.regulatorAssignmentClientId : -1,
        excludeDeactivatedFacilitiesImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.DeactivatedFacility)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.DeactivatedFacility)) ? true : undefined : false,
        excludeDeactivatedFacilitiesNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.DeactivatedFacility)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.DeactivatedFacility)) ? true : undefined : false,
        excludeDeactivatedProvidersImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.DeactivatedProvider)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.DeactivatedProvider)) ? true : undefined : false,
        excludeDeactivatedProvidersNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.DeactivatedProvider)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.DeactivatedProvider)) ? true : undefined : false,
        excludeInvalidNppesNpiFacilitiesImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.InvalidNppesNpiFacility)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.InvalidNppesNpiFacility)) ? true : undefined : false,
        excludeInvalidNppesNpiFacilitiesNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.InvalidNppesNpiFacility)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.InvalidNppesNpiFacility)) ? true : undefined : false,
        excludeInvalidNppesNpiProvidersImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.InvalidNppesNpiProvider)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.InvalidNppesNpiProvider)) ? true : undefined : false,
        excludeInvalidNppesNpiProvidersNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.InvalidNppesNpiProvider)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.InvalidNppesNpiProvider)) ? true : undefined : false,
        excludeMedicareOptOutProvidersImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.MedicareOptOut)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.MedicareOptOut)) ? true : undefined : false,
        excludeMedicareOptOutProvidersNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.MedicareOptOut)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.MedicareOptOut)) ? true : undefined : false,
        excludeMissingNpiFacilitiesImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.MissingNpiFacility)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.MissingNpiFacility)) ? true : undefined : false,
        excludeMissingNpiFacilitiesNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.MissingNpiFacility)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.MissingNpiFacility)) ? true : undefined : false,
        excludeMissingNpiProvidersImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.MissingNpiProvider)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.MissingNpiProvider)) ? true : undefined : false,
        excludeMissingNpiProvidersNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.MissingNpiProvider)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.MissingNpiProvider)) ? true : undefined : false,
        excludeOIGExcludedProvidersImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.OIGExclusion)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.OIGExclusion)) ? true : undefined : false,
        excludeOIGExcludedProvidersNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.OIGExclusion)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.OIGExclusion)) ? true : undefined : false,
        excludePrecludedProvidersImpact: impactExclusions.some((e) => e.includes(ProjectExclusionType.Precluded)) ? impactExclusions.every((e) => e.includes(ProjectExclusionType.Precluded)) ? true : undefined : false,
        excludePrecludedProvidersNetwork: networkExclusions.some((e) => e.includes(ProjectExclusionType.Precluded)) ? networkExclusions.every((e) => e.includes(ProjectExclusionType.Precluded)) ? true : undefined : false,
        strictTimeAndDistance: strictTimeAndDistances.length === 1 ? strictTimeAndDistances[0] ?? false : undefined,
        autoArchiveUnarchiveSpecialtyExceptions: autoArchiveUnarchiveSpecialtyExceptions.length === 1 ? autoArchiveUnarchiveSpecialtyExceptions[0] ?? false : undefined,
        showCMSCredit: showCMSCredits.length === 1 ? showCMSCredits[0] ?? false : undefined,
        includeGapAging: includeGapAgings.length === 1 ? includeGapAgings[0] ?? false : undefined,
        showAttestation: showAttestations.length === 1 ? showAttestations[0] ?? false : undefined,
        showNppesDates: showNppesDates.length === 1 ? showNppesDates[0] ?? false : undefined,
        showAdvancedComparison: showAdvancedComparisons.length === 1 ? showAdvancedComparisons[0] ?? false : undefined,
        showAverageDistance: showAverageDistances.length === 1 ? showAverageDistances[0] ?? false : undefined,
        showMaximumDistance: showMaximumDistances.length === 1 ? showMaximumDistances[0] ?? false : undefined,
        showAverageTime: showAverageTimes.length === 1 ? showAverageTimes[0] ?? false : undefined,
        showMaximumTime: showMaximumTimes.length === 1 ? showMaximumTimes[0] ?? false : undefined,
        hasZipSummaries: hasZipSummariesValue,
        showAffiliationsInsights: showAffiliationsInsights.length === 1 ? showAffiliationsInsights[0] ?? false : false,
        affiliationsDisplayOnNetworkAccordion: affiliationsDisplayOnNetworkAccordion.length === 1 ? affiliationsDisplayOnNetworkAccordion[0] ?? false : false,
        showNextClosestNetworkProviders: showNextClosestNetworkProvidersValue,
        showPatternOfCareProviders: showPatternOfCareProvidersValue,
        enablePatternOfCareProviders: getEnablePatternOfCare(hasZipSummariesValue, showNextClosestNetworkProvidersValue, hasEnablePatternOfCareProviders),
        enableRegulatoryExceptionReports: getEnableRegulatoryExceptionReports(hasZipSummariesValue, showNextClosestNetworkProvidersValue, hasEnableRegulatoryExceptionReport),
        showCareJourneyScores: showCareJourneyScores.length === 1 ? showCareJourneyScores[0] ?? false : undefined,
        showPgrConfidenceTier: showPgrConfidenceTier.length === 1 ? showPgrConfidenceTier[0] ?? false : undefined,
        showClosestProviderReport: showClosestProviderReports.length === 1 ? showClosestProviderReports[0] ?? false : undefined,
        showMipsScore: showMipsScores.length === 1 ? showMipsScores[0] ?? false : undefined,
        showPciData: showPciDatas.length === 1 ? showPciDatas[0] ?? false : undefined,
        showLocationConfidenceScores: showLocationConfidenceScores.length === 1 ? showLocationConfidenceScores[0] ?? false : undefined,
        showVaClaimsVolumeColumn: showVaClaimsVolumeColumn.length === 1 ? showVaClaimsVolumeColumn[0] ?? false : undefined,
        showTricareClaimsVolumeColumn: showTricareClaimsVolumeColumn.length === 1 ? showTricareClaimsVolumeColumn[0] ?? false : undefined,
        showMotiveAppropriatenessScore: showMotiveAppropriatenessScores.length === 1 ? showMotiveAppropriatenessScores[0] ?? false : undefined,
        excludeInVolumeActions: excludeInVolumeActions.length == 1 ? excludeInVolumeActions[0] ?? false : undefined,
        includeInEnterpriseComplianceDashboard: includeInEnterpriseComplianceDashboard.length == 1 ? includeInEnterpriseComplianceDashboard[0] ?? false : undefined,
        excludeProjectInSnowflakeDataShare: excludeProjectInSnowflakeDataShares.length === 1 ? excludeProjectInSnowflakeDataShares[0] ?? false : undefined,
        rppoNonContractedProvidersIncludedInHsd: rppoNonContractedProvidersIncludedInHsds.length === 1 ? rppoNonContractedProvidersIncludedInHsds[0] ?? false : undefined,
        showPciDashboard: showPciDashboards.length === 1 ? showPciDashboards[0] ?? false : undefined,
        useProspectProvidersForMarketAnalysis: useProspectProvidersForMarketAnalysis.length === 1 ? useProspectProvidersForMarketAnalysis[0] ?? false : undefined,
        useNetworkProvidersForMarketAnalysis: useNetworkProvidersForMarketAnalysis.length === 1 ? useNetworkProvidersForMarketAnalysis[0] ?? false : undefined,
        excludeAccuracyPractitionerAtLocationInspector: excludeAccuracyPractitionerAtLocationInspector.length === 1 ? excludeAccuracyPractitionerAtLocationInspector[0] ?? false : undefined,
        excludeProviderClaimsActivityInspector: excludeProviderClaimsActivityInspector.length === 1 ? excludeProviderClaimsActivityInspector[0] ?? false : undefined,
        showNetworkIntegrityDashboard: showNetworkIntegrityDashboards.length === 1 ? showNetworkIntegrityDashboards[0] ?? false : undefined,
        showNetworkIntegrityCriticalAlerts: showNetworkIntegrityCriticalAlerts.length === 1 ? showNetworkIntegrityCriticalAlerts[0] ?? false : undefined,
        showNetworkIntegrityAttestationBasedAlerts: showNetworkIntegrityAttestationBasedAlerts.length === 1 ? showNetworkIntegrityAttestationBasedAlerts[0] ?? false : undefined,
        useSnowflakeProviderSetsForAnalysis: useSnowflakeProviderSetsForAnalysis.length === 1 ? useSnowflakeProviderSetsForAnalysis[0] ?? false : undefined,
        /* oxlint-enable sort-keys */
      });
    });
  }

  const getEnablePatternOfCare = (hasZipSummariesValue: boolean, showNextClosestNetworkProvidersValue: boolean, hasEnablePatternOfCareProviders: boolean[]): boolean => {
    if (hasZipSummariesValue && showNextClosestNetworkProvidersValue && hasEnablePatternOfCareProviders.every(value => value == false)) {
      return false;
    } else if (hasZipSummariesValue && showNextClosestNetworkProvidersValue && hasEnablePatternOfCareProviders.every(value => value == true)) {
      return true;
    } else {
      return hasEnablePatternOfCareProviders.length === 1 ? hasEnablePatternOfCareProviders[0] ?? false : undefined;
    }
  }

  const getEnableRegulatoryExceptionReports = (hasZipSummariesValue: boolean, showNextClosestNetworkProvidersValue: boolean, hasEnableRegulatoryExceptionReport: boolean[]): boolean => {
    if (hasZipSummariesValue && showNextClosestNetworkProvidersValue && hasEnableRegulatoryExceptionReport.every(value => value == false)) {
      return false;
    } else if (hasZipSummariesValue && showNextClosestNetworkProvidersValue && hasEnableRegulatoryExceptionReport.every(value => value == true)) {
      return true;
    } else {
      return hasEnableRegulatoryExceptionReport.length === 1 ? hasEnableRegulatoryExceptionReport[0] ?? false : undefined;
    }
  }

  const handleTemplateCollectionChange = (templateCollectionId: number) => {
    templateRequestRef.current++;

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      accessCodeMapSetId: null,
      adequacyTemplateCollectionId: templateCollectionId,
      adequacyTemplateCorrelationId: "",
      excludedSpecialtyGroupCodes: [],
      timeDistanceCalculatorType: TimeDistanceCalculatorType.Unknown,
    }));

    setTimeDistanceCalculatorTypes([]);
    setProjectAccessCodeMapSets([]);
    setSortedSpecialtyGroups([]);
    setProjectMemberPopulations([]);
  }

  const handleTemplateCorrelationIdChange = async (templateCorrelationId: string, userRoles: string[]) => {
    const requestId = ++templateRequestRef.current;
    if (templateCorrelationId === "0") {
      setVolumeChange((prevVolumeChange) => ({
        ...prevVolumeChange,
        adequacyTemplateCorrelationId: templateCorrelationId,
        disagreedExcludedSpecialtyGroupCodes: [],
        disagreedTelehealthSpecialtyGroupCodes: [],
        excludedSpecialtyGroupCodes: [],
        telehealthSpecialtyGroupCodes: [],
        timeDistanceCalculatorType: TimeDistanceCalculatorType.Unknown,
      }));
      setProjectAccessCodeMapSets([]);
      setSortedSpecialtyGroups([]);
      setProjectMemberPopulations([]);
    } else {
      const templateInfo = await getTemplateInfo(project.adequacyTemplateCollectionId, templateCorrelationId);
      if (requestId !== templateRequestRef.current) return;
      applyTemplateInfo(templateInfo);
      const hasCmsApplicationCredits: boolean = templateInfo.specialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);
      setHideCMSCredit(!hasCmsApplicationCredits);

      const showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId !== null;
      setHideMotiveAppropriatenessScore(!showMotiveAppropriatenessScore);
      const isZipTemplate = templateCollections.find((tc) => tc.templateCollectionId === project.adequacyTemplateCollectionId).resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
      setIsZipProject(zipProjectTypeSelected || isZipTemplate);

      const excludedSpecialtyGroupCodes: string[] = [];
      const disagreedExcludedSpecialtyGroupCodes: string[] = [];
      const includedSpecialtyGroupCodes: string[] = templateInfo.specialtyGroups.map(sg => sg.code)

      const telehealthSpecialtyGroupCodes: string[] = [];
      const disagreedTelehealthSpecialtyGroupCodes: string[] = [];

      setVolumeChange((prevVolumeChange) => ({
        ...prevVolumeChange,
        adequacyTemplateCorrelationId: templateCorrelationId,
        disagreedExcludedSpecialtyGroupCodes: disagreedExcludedSpecialtyGroupCodes,
        disagreedTelehealthSpecialtyGroupCodes: disagreedTelehealthSpecialtyGroupCodes,
        excludedSpecialtyGroupCodes: excludedSpecialtyGroupCodes,
        showCMSCredit: !hasCmsApplicationCredits ? false : volumeChange.showCMSCredit,
        showMotiveAppropriatenessScore: !showMotiveAppropriatenessScore ? false : volumeChange.showMotiveAppropriatenessScore,
        specialtyGroupCodesToInclude: includedSpecialtyGroupCodes,
        telehealthSpecialtyGroupCodes: telehealthSpecialtyGroupCodes,
        timeDistanceCalculatorType: templateHelper.getTimeDistanceCalculatorType(templateInfo, userRoles, prevVolumeChange.timeDistanceCalculatorType)
      }));
    }
  }

  const handleTimeDistanceCalculatorTypeChange = (timeDistanceCalculatorType: TimeDistanceCalculatorType) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      timeDistanceCalculatorType: timeDistanceCalculatorType
    }));
  }

  const handleAccessCodeMapSetChange = (accessCodeMapSetId: number) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      accessCodeMapSetId: accessCodeMapSetId ? accessCodeMapSetId : null
    }));
  }

  const handleNetworkSpecialtyCodeMapChange = (networkSpecialtyCodeMapSetId: number) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      networkSpecialtyCodeMapSetId: networkSpecialtyCodeMapSetId || null
    }));
  }

  const handleMarketSpecialtyCodeMapChange = (marketSpecialtyCodeMapSetId: number) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      marketSpecialtyCodeMapSetId: marketSpecialtyCodeMapSetId || null
    }));
  }

  const handleExpandSpecialtyMileageChange = (expandSpecialtyMileageCodeMapSetId: number) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      expandSpecialtyMileageCodeMapSetId: expandSpecialtyMileageCodeMapSetId || null
    }));
  }

  const handleSpecialtyGroupsChange = (selectedSpecialtyGroups: TemplateSpecialtyGroup[]) => {
    const specialtyGroupCodes = sortedSpecialtyGroups.map((sg) => sg.code);
    const selectedSpecialtyGroupCodes = selectedSpecialtyGroups.map((sg) => sg.code);
    const excludedSpecialtyGroupCodes = difference(specialtyGroupCodes, selectedSpecialtyGroupCodes);
    const disagreedExcludedSpecialtyGroupCodes: string[] = volumeChange.disagreedExcludedSpecialtyGroupCodes.filter(sg => !selectedSpecialtyGroupCodes.includes(sg));

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      disagreedExcludedSpecialtyGroupCodes: disagreedExcludedSpecialtyGroupCodes,
      excludedSpecialtyGroupCodes: excludedSpecialtyGroupCodes,
    }));
  }

  const handleTelelhealthSpecialtyGroupChange = (selectedSpecialtyGroupCodes: string[]) => {
    const disagreedTelehealthSpecialtyGroupCodes: string[] = volumeChange.disagreedTelehealthSpecialtyGroupCodes.filter(sg => !selectedSpecialtyGroupCodes.includes(sg));

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      disagreedTelehealthSpecialtyGroupCodes: disagreedTelehealthSpecialtyGroupCodes,
      telehealthSpecialtyGroupCodes: selectedSpecialtyGroupCodes
    }));
  }

  const isResetDisabled = () => {
    const templateDisabled = volumeInitial?.adequacyTemplateCollectionId === volumeChange?.adequacyTemplateCollectionId &&
      volumeInitial?.adequacyTemplateCorrelationId === volumeChange?.adequacyTemplateCorrelationId &&
      volumeInitial?.timeDistanceCalculatorType === volumeChange?.timeDistanceCalculatorType &&
      volumeInitial?.networkSpecialtyCodeMapSetId === volumeChange?.networkSpecialtyCodeMapSetId &&
      volumeInitial?.marketSpecialtyCodeMapSetId === volumeChange?.marketSpecialtyCodeMapSetId &&
      volumeInitial?.accessCodeMapSetId === volumeChange?.accessCodeMapSetId &&
      volumeInitial?.excludedSpecialtyGroupCodes.length === volumeChange?.excludedSpecialtyGroupCodes.length &&
      volumeInitial?.excludedSpecialtyGroupCodes.every(sg => volumeChange?.excludedSpecialtyGroupCodes.includes(sg)) &&
      volumeInitial?.disagreedExcludedSpecialtyGroupCodes.length === volumeChange?.disagreedExcludedSpecialtyGroupCodes.length &&
      volumeInitial?.disagreedExcludedSpecialtyGroupCodes.every(sg => volumeChange?.disagreedExcludedSpecialtyGroupCodes.includes(sg)) &&
      volumeInitial?.telehealthSpecialtyGroupCodes.length === volumeChange?.telehealthSpecialtyGroupCodes.length &&
      volumeInitial?.telehealthSpecialtyGroupCodes.every(sg => volumeChange?.telehealthSpecialtyGroupCodes.includes(sg)) &&
      volumeInitial?.disagreedTelehealthSpecialtyGroupCodes.length === volumeChange?.disagreedTelehealthSpecialtyGroupCodes.length &&
      volumeInitial?.disagreedTelehealthSpecialtyGroupCodes.every(sg => volumeChange?.disagreedTelehealthSpecialtyGroupCodes.includes(sg));

    setTemplateResetDisabled(templateDisabled);

    const memberDisabled = volumeInitial?.memberSetId === volumeChange?.memberSetId &&
      volumeInitial?.memberSetAlias === volumeChange?.memberSetAlias &&
      volumeInitial?.memberSetId2 === volumeChange?.memberSetId2 &&
      volumeInitial?.memberSetAlias2 === volumeChange?.memberSetAlias2;

    setMembersResetDisabled(memberDisabled);

    const providerDisable = volumeInitial?.networkProviderSetId === volumeChange?.networkProviderSetId &&
      volumeInitial?.prospectProviderSetId === volumeChange?.prospectProviderSetId &&
      volumeInitial?.marketProviderSetId === volumeChange?.marketProviderSetId &&
      volumeInitial?.useMarketProvidersForImpact === volumeChange?.useMarketProvidersForImpact &&
      volumeInitial?.useNetworkProvidersForMarketAnalysis === volumeChange?.useNetworkProvidersForMarketAnalysis &&
      volumeInitial?.useProspectProvidersForMarketAnalysis === volumeChange?.useProspectProvidersForMarketAnalysis;

    setProvidersResetDisabled(providerDisable);

    const networkTaxonomyDisabled = volumeInitial?.fileTypeId === volumeChange?.fileTypeId &&
      volumeInitial?.fileTypeReason === volumeChange?.fileTypeReason &&
      volumeInitial?.regulatorAssignmentClientId === volumeChange?.regulatorAssignmentClientId &&
      volumeInitial?.projectUtilizationCategoryId === volumeChange?.projectUtilizationCategoryId &&
      volumeInitial?.lineOfBusinessId === volumeChange?.lineOfBusinessId &&
      volumeInitial?.lineOfBusinessTypeId === volumeChange?.lineOfBusinessTypeId &&
      volumeInitial?.lineOfBusinessReason === volumeChange?.lineOfBusinessReason;

    setNetworkTaxonomyResetDisabled(networkTaxonomyDisabled);

    let optionsDisable = true;
    options.forEach(option => {
      optionsDisable = optionsDisable && (volumeInitial[option.key] === volumeChange[option.key]);
    })

    setOptionsResetDisabled(optionsDisable);
  }

  const handleTemplateReset = async () => {
    const requestId = ++templateRequestRef.current;
    let hasCmsApplicationCredits = false;
    let isZipTemplate = false;
    if (volumeInitial.adequacyTemplateCorrelationId !== "0" && volumeInitial.adequacyTemplateCorrelationId !== "-1") {
      const templateInfo = await getTemplateInfo(volumeInitial.adequacyTemplateCollectionId, volumeInitial.adequacyTemplateCorrelationId);
      if (requestId !== templateRequestRef.current) return;
      applyTemplateInfo(templateInfo);
      hasCmsApplicationCredits = templateInfo.specialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);
      isZipTemplate = templateCollections.find((tc) => tc.templateCollectionId === project.adequacyTemplateCollectionId).resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
    }
    else {
      setTimeDistanceCalculatorTypes([]);
      setProjectAccessCodeMapSets([]);
      setTemplateAccessGranularity(AccessCriteriaGranularityEnum.Unknown);
      setSortedSpecialtyGroups(null);
      setProjectMemberPopulations(null);
    }

    setIsZipProject(zipProjectTypeSelected || isZipTemplate);
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      accessCodeMapSetId: volumeInitial.accessCodeMapSetId,
      adequacyTemplateCollectionId: volumeInitial.adequacyTemplateCollectionId,
      adequacyTemplateCorrelationId: volumeInitial.adequacyTemplateCorrelationId,
      disagreedExcludedSpecialtyGroupCodes: volumeInitial.disagreedExcludedSpecialtyGroupCodes,
      disagreedTelehealthSpecialtyGroupCodes: volumeInitial.disagreedTelehealthSpecialtyGroupCodes,
      excludedSpecialtyGroupCodes: volumeInitial.excludedSpecialtyGroupCodes,
      marketSpecialtyCodeMapSetId: volumeInitial.marketSpecialtyCodeMapSetId,
      networkSpecialtyCodeMapSetId: volumeInitial.networkSpecialtyCodeMapSetId,
      showCMSCredit: volumeInitial.showCMSCredit,
      telehealthSpecialtyGroupCodes: volumeInitial.telehealthSpecialtyGroupCodes,
      timeDistanceCalculatorType: volumeInitial.timeDistanceCalculatorType,
    }));

    setHideCMSCredit(!hasCmsApplicationCredits);
  }

  const getTemplateInfo = async (templateCollectionId: number, templateCorrelationId: string, codeMapSets?: CodeMapSet[], memberPopulations?: MemberSetModel[], collections?: TemplateCollectionMetadata[]) => {
    return await templateHelper.getTemplateInfo(
      new InitialProjectState(),
      codeMapSets ?? allAccessCodeMapSets,
      memberPopulations ?? allMemberPopulations,
      collections ?? templateCollections,
      templateCollectionId,
      templateCorrelationId,
      project.templateId);
  }

  const applyTemplateInfo = (templateInfo: Awaited<ReturnType<typeof getTemplateInfo>>) => {
    setTimeDistanceCalculatorTypes(templateInfo.timeDistanceCalculatorTypes);
    setProjectAccessCodeMapSets(templateInfo.projectAccessCodeMapSets);
    setTemplateAccessGranularity(templateInfo.templateAccessGranularity);
    setSortedSpecialtyGroups(templateInfo.specialtyGroups && sortBy(templateInfo.specialtyGroups, sortOrder));
    setProjectMemberPopulations(templateInfo.projectMemberPopulations);
  }

  const handleMemberPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberSetId = +(e.target.value)

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      memberSetAlias: memberSetId === 0 ? "" : volumeChange.memberSetAlias,
      memberSetId: memberSetId,
    }));
  }

  const handleMemberPopAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      memberSetAlias: e.target.value,
    }));
  }

  const handleMemberPopChange2 = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberSetId2 = +(e.target.value)

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      memberSetAlias2: memberSetId2 === 0 ? "" : volumeChange.memberSetAlias2,
      memberSetId2: memberSetId2,
    }));
  }

  const handleMemberPopAliasChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      memberSetAlias2: e.target.value,
    }));
  }

  const handleMembersReset = () => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      memberSetAlias: volumeInitial.memberSetAlias,
      memberSetAlias2: volumeInitial.memberSetAlias2,
      memberSetId: volumeInitial.memberSetId,
      memberSetId2: volumeInitial.memberSetId2,
    }));
  }

  const getEditProjectOverrideMemberSet = (): EditProjectOverrideMemberSet[] => {
    const editProjectOverrideMemberSets = [newEditProjectOverrideMemberSet(ProjectOverrideMemberSetType.Primary)];
    editProjectOverrideMemberSets.push(newEditProjectOverrideMemberSet(ProjectOverrideMemberSetType.Secondary));

    return editProjectOverrideMemberSets;
  }

  const newEditProjectOverrideMemberSet = (memberSetType: ProjectOverrideMemberSetType): EditProjectOverrideMemberSet => {
    return {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: memberSetType,
      secondarySpecialtyGroupCodes: [],
    };
  }

  const handleProvidersReset = () => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      marketProviderSetAlias: "",
      marketProviderSetId: volumeInitial.marketProviderSetId,
      networkProviderSetAlias: "",
      networkProviderSetId: volumeInitial.networkProviderSetId,
      prospectProviderSetAlias: "",
      prospectProviderSetId: volumeInitial.prospectProviderSetId,
      useMarketProvidersForImpact: volumeInitial.useMarketProvidersForImpact,
      useNetworkProvidersForMarketAnalysis: volumeInitial.useNetworkProvidersForMarketAnalysis,
      useProspectProvidersForMarketAnalysis: volumeInitial.useProspectProvidersForMarketAnalysis
    }));
  }

  const handleMarketChange = (value: string) => {
    const marketProviderSetId = value ? Number(value) : null;

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      marketProviderSetId: marketProviderSetId,
      useMarketProvidersForImpact: !marketProviderSetId ? false : true
    }));
  }

  const handleProspectsChange = (value: string) => {
    const prospectProviderSetId = value ? Number(value) : null;

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      prospectProviderSetId: value ? Number(value) : null,
      useProspectProvidersForMarketAnalysis:
        !!prevVolumeChange.prospectProviderSetId && !!prospectProviderSetId ?
          prevVolumeChange.useProspectProvidersForMarketAnalysis :
          !!prevVolumeChange.prospectProviderSetId && !prospectProviderSetId ?
            false : volumeInitial.useProspectProvidersForMarketAnalysis


    }));
  }

  const handleProvidersChange = (value: string) => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      networkProviderSetId: value ? Number(value) : null,
    }));
  }

  const handleNetworkProvidersAliasChange = (alias: string) => {
    const newChanges = { ...volumeChange };
    newChanges.networkProviderSetAlias = alias;
    setVolumeChange(newChanges);
  }

  const handleProspectProvidersAliasChange = (alias: string) => {
    const newChanges = { ...volumeChange };
    newChanges.prospectProviderSetAlias = alias;
    setVolumeChange(newChanges);
  }

  const handleMarketProvidersAliasChange = (alias: string) => {
    const newChanges = { ...volumeChange };
    newChanges.marketProviderSetAlias = alias;
    setVolumeChange(newChanges);
  }

  const handleNcpAliasChange = (alias: string) => {
    const newChanges = { ...volumeChange };
    const shouldEnableNcp = !!alias || newChanges.showNextClosestNetworkProviders;
    newChanges.nextClosestProviderSetAlias = alias;
    newChanges.showNextClosestNetworkProviders = shouldEnableNcp;
    setVolumeChange(newChanges);
  }

  const handleNetworkTaxonomyReset = () => {
    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      fileTypeId: volumeInitial.fileTypeId,
      fileTypeReason: volumeInitial.fileTypeReason,
      lineOfBusinessId: volumeInitial.lineOfBusinessId,
      lineOfBusinessReason: volumeInitial.lineOfBusinessReason,
      lineOfBusinessTypeId: volumeInitial.lineOfBusinessTypeId,
      projectUtilizationCategoryId: volumeInitial.projectUtilizationCategoryId,
      regulatorAssignmentClientId: volumeInitial.regulatorAssignmentClientId,
    }));
    setIsNetworkTaxonomyValid(true);
  }

  const handleNetworkTaxonomyChange = (networkTaxonomy: NetworkTaxonomyData, isValid: boolean, invalidProperties: BulkProjectEditChangeReason[]) => {
    if (loadingSelectedProjects)
      return;

    setIsNetworkTaxonomyValid(isValid);
    setInvalidNetworkTaxonomyProperties(invalidProperties);

    if (!project.networkTaxonomy ||
      // Stop Circular reference update
      (volumeChange.fileTypeId === networkTaxonomy.fileTypeId &&
        volumeChange.fileTypeReason === networkTaxonomy.fileTypeReason &&
        volumeChange.regulatorAssignmentClientId === networkTaxonomy.regulatorAssignmentClientId &&
        volumeChange.projectUtilizationCategoryId === networkTaxonomy.projectUtilizationCategoryId &&
        volumeChange.lineOfBusinessId === networkTaxonomy.lineOfBusinessId &&
        volumeChange.lineOfBusinessTypeId === networkTaxonomy.lineOfBusinessTypeId &&
        volumeChange.lineOfBusinessReason === networkTaxonomy.reason))
      return;

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      fileType: networkTaxonomy?.fileType,
      fileTypeId: networkTaxonomy?.fileTypeId,
      fileTypeReason: networkTaxonomy?.fileTypeReason,
      lineOfBusiness: networkTaxonomy?.lineOfBusiness,
      lineOfBusinessId: networkTaxonomy?.lineOfBusinessId,
      lineOfBusinessReason: networkTaxonomy?.reason,
      lineOfBusinessType: networkTaxonomy?.lineOfBusinessType,
      lineOfBusinessTypeId: networkTaxonomy?.lineOfBusinessTypeId,
      projectUtilizationCategory: networkTaxonomy?.projectUtilizationCategory,
      projectUtilizationCategoryId: networkTaxonomy?.projectUtilizationCategoryId,
      regulatorAssignmentClient: networkTaxonomy?.regulatorAssignmentClient,
      regulatorAssignmentClientId: networkTaxonomy?.regulatorAssignmentClientId,
    }));
  }

  const handleOptionsReset = () => {
    const optionReset: BulkProjectEditChange = { ...volumeChange };
    options.forEach(option => {
      (optionReset[option.key as keyof BulkProjectEditChange] as boolean) = (volumeInitial[option.key as keyof BulkProjectEditChange] as boolean);
    })
    setVolumeChange(optionReset);
  }

  const handleOptionsCheckBoxChange = (property: UpdateResourceKeys) => {
    // if volumeInitial[property] is true, null, or undefined, at least one project had it checked
    if (property === "includeInEnterpriseComplianceDashboard" && !!volumeChange[property] && volumeInitial[property] !== false) {
      setWarningForDisableDescription("Disabling this option will cause the selected projects that had this option enabled to be immediately removed from the Enterprise Compliance Dashboard when the changes are saved.");
      setWarningForDisableOption(property);
    } else {
      toggleOptionsCheckbox(property);
    }
  }

  const handleProvidersCheckBoxChange = (property: UpdateResourceKeys) => {

    setVolumeChange((prevVolumeChange) => ({
      ...prevVolumeChange,
      /* oxlint-disable sort-keys */
      [property]: !volumeChange[property],
      /* oxlint-enable */
    }));

    setWarningForDisableDescription("");
    setWarningForDisableOption(null);
  }

  const toggleOptionsCheckbox = (property: UpdateResourceKeys) => {
    const newProjectModel: BulkProjectEditChange = {
      ...volumeChange,
      [property]: !volumeChange[property],
    };

    // if showPciData is being changed, turn off showPciDashboard, showLocationConfidenceScores, and showMotiveAppropriatenessScore, else keep current state
    if (property === "showPciData") {
      newProjectModel.showCareJourneyScores = false;
      newProjectModel.showLocationConfidenceScores = false;
      newProjectModel.showMotiveAppropriatenessScore = false;
      newProjectModel.showPciDashboard = false;
      newProjectModel.showTricareClaimsVolumeColumn = false;
      newProjectModel.showVaClaimsVolumeColumn = false;
    }

    if (property === "showNetworkIntegrityDashboard") {
      // default to turning off critical and attestation-based alerts when showing the NI dashboard
      newProjectModel.showNetworkIntegrityCriticalAlerts = false;
      newProjectModel.showNetworkIntegrityAttestationBasedAlerts = false;
    }

    /* If showNextClosestNetworkProviders or hasZipSummaries are being changed,
    and if either it was true or one of those has been set to false and enablePatternOfCareProviders is indeterminate, turn showPatternOfCareProviders off */
    if ((property === "showNextClosestNetworkProviders" || property === "hasZipSummaries") &&
      (!!newProjectModel.showPatternOfCareProviders ||
        (!!volumeChange[property] && newProjectModel.showPatternOfCareProviders == undefined))) {
      newProjectModel.showPatternOfCareProviders = false;
    }

    /* If showNextClosestNetworkProviders or hasZipSummaries are being changed,
      and if either it was true or one of those has been set to false and enableRegulatoryException is indeterminate, turn enableRegulatoryExceptionReports off */
    if ((property === "showNextClosestNetworkProviders" || property === "hasZipSummaries") &&
      (!!newProjectModel.enableRegulatoryExceptionReports ||
        (!!volumeChange[property] && newProjectModel.enableRegulatoryExceptionReports == undefined))) {
      newProjectModel.enableRegulatoryExceptionReports = false;
    }

    if (property === "showNextClosestNetworkProviders" && volumeChange[property]) {
      newProjectModel.nextClosestProviderSetAlias = "";
    }

    setVolumeChange(newProjectModel);
    setWarningForDisableDescription("");
    setWarningForDisableOption(null);
  }

  const handleImpactExclusionsChange = (exclusion: ProjectExclusionType) => {
    switch (exclusion) {
      case ProjectExclusionType.DeactivatedFacility:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeDeactivatedFacilitiesImpact: !volumeChange.excludeDeactivatedFacilitiesImpact,
        }));
        break;
      case ProjectExclusionType.DeactivatedProvider:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeDeactivatedProvidersImpact: !volumeChange.excludeDeactivatedProvidersImpact,
        }));
        break;
      case ProjectExclusionType.InvalidNppesNpiFacility:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeInvalidNppesNpiFacilitiesImpact: !volumeChange.excludeInvalidNppesNpiFacilitiesImpact,
        }));
        break;
      case ProjectExclusionType.InvalidNppesNpiProvider:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeInvalidNppesNpiProvidersImpact: !volumeChange.excludeInvalidNppesNpiProvidersImpact,
        }));
        break;
      case ProjectExclusionType.MissingNpiFacility:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeMissingNpiFacilitiesImpact: !volumeChange.excludeMissingNpiFacilitiesImpact,
        }));
        break;
      case ProjectExclusionType.MissingNpiProvider:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeMissingNpiProvidersImpact: !volumeChange.excludeMissingNpiProvidersImpact,
        }));
        break;
      case ProjectExclusionType.MedicareOptOut:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeMedicareOptOutProvidersImpact: !volumeChange.excludeMedicareOptOutProvidersImpact,
        }));
        break;
      case ProjectExclusionType.OIGExclusion:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeOIGExcludedProvidersImpact: !volumeChange.excludeOIGExcludedProvidersImpact,
        }));
        break;
      case ProjectExclusionType.Precluded:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludePrecludedProvidersImpact: !volumeChange.excludePrecludedProvidersImpact,
        }));
        break;
    }
  }

  const handleNetworkExclusionsChange = (exclusion: ProjectExclusionType) => {
    switch (exclusion) {
      case ProjectExclusionType.DeactivatedFacility:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeDeactivatedFacilitiesNetwork: !volumeChange.excludeDeactivatedFacilitiesNetwork,
        }));
        break;
      case ProjectExclusionType.DeactivatedProvider:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeDeactivatedProvidersNetwork: !volumeChange.excludeDeactivatedProvidersNetwork,
        }));
        break;
      case ProjectExclusionType.InvalidNppesNpiFacility:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeInvalidNppesNpiFacilitiesNetwork: !volumeChange.excludeInvalidNppesNpiFacilitiesNetwork,
        }));
        break;
      case ProjectExclusionType.InvalidNppesNpiProvider:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeInvalidNppesNpiProvidersNetwork: !volumeChange.excludeInvalidNppesNpiProvidersNetwork,
        }));
        break;
      case ProjectExclusionType.MissingNpiFacility:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeMissingNpiFacilitiesNetwork: !volumeChange.excludeMissingNpiFacilitiesNetwork,
        }));
        break;
      case ProjectExclusionType.MissingNpiProvider:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeMissingNpiProvidersNetwork: !volumeChange.excludeMissingNpiProvidersNetwork,
        }));
        break;
      case ProjectExclusionType.MedicareOptOut:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeMedicareOptOutProvidersNetwork: !volumeChange.excludeMedicareOptOutProvidersNetwork,
        }));
        break;
      case ProjectExclusionType.OIGExclusion:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludeOIGExcludedProvidersNetwork: !volumeChange.excludeOIGExcludedProvidersNetwork,
        }));
        break;
      case ProjectExclusionType.Precluded:
        setVolumeChange((prevVolumeChange) => ({
          ...prevVolumeChange,
          excludePrecludedProvidersNetwork: !volumeChange.excludePrecludedProvidersNetwork,
        }));
        break;
    }
  }

  const getTemplateCollectionDisplayByVolumeChangeId = () => {
    const collectionResource = templateCollections.find(c => c.templateCollectionId === volumeChange.adequacyTemplateCollectionId)?.resource;
    return `${collectionResource?.name} - ${collectionResource?.description}`
  }

  const handleSaveChanges = (runNow: boolean) => {
    setSaveChangesClicked(true);
    volumeChange.fileTypeId = volumeChange.fileTypeId === -2 ? null : volumeChange.fileTypeId;
    projectService.updateProjects(volumeChange)
      .then(() => {
        toast("Update Successful!", {
          containerId: "z-index-9999",
          type: "success",
        });

        if (runNow) {
          projectService.bulkRun(volumeChange.selectedProjects)
            .then((response) => {
              if (response.submitResults.length === 0)
                errorToast("The request to run projects could not be completed.");
              else {
                let newStatus = "";
                let toastType: TypeOptions = "default";
                if (response.submitResults.length < volumeChange.selectedProjects.length) {
                  newStatus = "Only some of the projects were queued to run successfully.";
                  toastType = "warning";
                } else if (response.submitResults.length === volumeChange.selectedProjects.length) {
                  newStatus = "Projects Queued to Run Successfully!";
                  toastType = "success";
                }
                toast(newStatus, {
                  containerId: "z-index-9999",
                  type: toastType,
                });
                refreshEditProjects();
              }
            })
            .catch(() => {
              errorToast("The request to run projects could not be completed.");
            });
        }
        else
          refreshEditProjects();
      })
      .catch(() => {
        errorToast("Failed to update the selected projects. Please try again.");
      })
  }

  const refreshEditProjects = () => {
    setLoading(true);
    setSelectedProjects([]);
    getEditableProjects();
    setActiveEditModule(ActiveEditModule.Projects);
    setSaveChangesClicked(false);
  }

  const handleProjectTypeChange = (item: DropdownItem<string>) => {
    if (!item || item.key === projectType) return;
    // Invalidate any in-flight selected-project load for the previous project type
    selectedProjectsRequestRef.current++;
    // Invalidate any in-flight dashboard-filter request for the previous project type
    dashboardFilterRequestRef.current++;
    // Invalidate any in-flight template operation for the previous project type
    templateRequestRef.current++;
    setProjectType(item.key);
    setLoading(true);

    // Changing the project population invalidates every selection and edit made for the previous one
    setSelectedProjects([]);
    setProjectSelectionChanged(false);
    setFilter([]);
    setSelectedItems([]);
    setCanApplyDashboardFilters(false);
    setDashboardFilterResourceIds([]);
    setLineOfBusinessFilterOptionsDisabled(false);
    setProjects(undefined);
    setVolumeInitial(undefined);
    setVolumeChange(undefined);
    setLoadingSelectedProjects(true);
    setIsZipProject(item.key === PROJECT_TYPE_ZIP_CODE);
    setIsValid(true);
    setIsProviderSetsValid(true);
    setHasMixedTaxonomy(false);
    setSaveChangesClicked(false);
    setTemplateChangeCount(0);
    setMembersChangeCount(0);
    setProvidersChangeCount(0);
    setNetworkTaxonomyChangeCount(0);
    setOptionsChangeCount(0);
    setTemplateResetDisabled(true);
    setMembersResetDisabled(true);
    setProvidersResetDisabled(true);
    setNetworkTaxonomyResetDisabled(true);
    setOptionsResetDisabled(true);

    getEditableProjects(item.key);
  };

  const errorToast = (message: string) => {
    toast(message, {
      /* oxlint-disable sort-keys */
      containerId: "z-index-9999",
      type: "error",
      onClose: () => { setSaveChangesClicked(false); }
      /* oxlint-enable sort-keys */
    });
  }

  const customSort = (records: BulkEditModelTable[], column: keyof BulkEditModelTable, direction: string) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "name":
      case "lastRunStatus":
      case "template":
      case "networkName":
      case "primaryMembershipName":
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      case "lastRunDate":
        return sortByDate(data, "lastRunDate", direction, "name");
      default:
        return data;
    }
  }

  // #4fa0ff is the same as $qes-tertiary-blue-2 in scss
  const renderProviderSetChange = (providerSetType: string, initialProviderSetId: number | undefined, newProviderSetId: number | undefined, newProviderSetName: string | undefined) => {
    let iconName: QesIcons;
    let iconDescription: string;
    let color: string;
    if (initialProviderSetId == 0 && newProviderSetId > 0) {
      iconName = QesIcons.Plus;
      iconDescription = "Added";
      color = "green";
    } else if (initialProviderSetId != 0 && newProviderSetId == undefined) {
      iconName = QesIcons.Minus;
      iconDescription = "Removed";
      color = "red";
    } else {
      iconName = QesIcons.PencilV1;
      iconDescription = "Changed";
      color = "#4FA0FF";
    }

    return (
      <div className="form-group row">
        <QesIcon iconName={iconName} description={iconDescription} className="mt2" color={color} />
        <label className="col-4 demibold">{providerSetType} Provider:</label>
        <div className="col-4" style={{ color }}>{newProviderSetName ?? "(Removed)"}</div>
      </div>
    );
  }

  const renderProviderSetAliasChange = (providerSetType: string, initialValue: string | undefined, newValue: string | undefined) => {
    let iconName: QesIcons;
    let iconDescription: string;
    let color: string;
    if (!initialValue) {
      iconName = QesIcons.Plus;
      iconDescription = "Added";
      color = "green";
    } else if (!!initialValue && !newValue) {
      iconName = QesIcons.Minus;
      iconDescription = "Removed";
      color = "red";
    } else {
      iconName = QesIcons.PencilV1;
      iconDescription = "Changed";
      color = "#4FA0FF";
    }

    return (
      <div className="form-group row">
        <QesIcon iconName={iconName} description={iconDescription} className="mt2" color={color} />
        <label className="col-4 demibold">{providerSetType} Provider Alias:</label>
        <div className="col-4" style={{ color }}>{newValue || "(Removed)"}</div>
      </div>
    );
  }

  const renderProviderBooleanChange = (providerSetType: string, newValue: boolean) => {
    let iconName: QesIcons;
    let color: string;
    if (newValue) {
      iconName = QesIcons.Plus;
      color = "green";
    } else {
      iconName = QesIcons.Minus;
      color = "red";
    }

    return (
      <div className="form-group row">
        <QesIcon iconName={iconName} isDecorative className="mt2" color={color} />
        <label className="col-4 demibold">Use {providerSetType} Providers For Market Analysis:</label>
        <div className="col-4" style={{ color }}>{newValue ? "(Added)" : "(Removed)"}</div>
      </div>
    );
  }

  const lineOfBusinessFilterChange = (response: DropdownItem<unknown>[]) => {
    setCanApplyDashboardFilters(false);
    setSelectedItems(response);
    setFilter(response);
  }

  useEffect(() => {
    if (filter !== undefined && filter.length > 0) {
      const filteredList = [] as BulkEditModelTable[];
      filter.forEach(element => {
        editableProjects.forEach(row => {
          if (element.key === row.lineOfBusiness) {
            filteredList.push(row);
          }
        });
      });
      setFilteredEditableProjects(filteredList);
      pruneSelectionToVisible(filteredList);
    } else {
      setFilteredEditableProjects(editableProjects);
      pruneSelectionToVisible(editableProjects);
    }
  }, [editableProjects, filter]);

  // Deselect any selected project the Line of Business filter hides so it does not carry into Summary or Save
  const pruneSelectionToVisible = (visibleRows: BulkEditModelTable[]) => {
    const visibleKeys = new Set(visibleRows.map((row) => row.key));
    const prunedKeys = selectedProjects.filter((key) => visibleKeys.has(key));
    if (prunedKeys.length !== selectedProjects.length) {
      handleCheckboxChange(prunedKeys);
    }
  };

  useEffect(() => {
    networkTaxonomyService.getLinesOfBusiness().then(data => {
      const dropdownItems: DropdownItem<unknown>[] = data.map((x) => ({
        display: x.name,
        key: x.lineOfBusinessId,
        name: x.name
      }));
      setLineOfBusinessFilterOptions(dropdownItems);
    })
  }, []);

  const renderBulkProjectsEdit = () => {
    return (
      <>
        {activeEditModule === ActiveEditModule.Projects &&
          <>
            {zipBulkEditEnabled && (
              <SingleSelectDropdown
                hideSearch={true}
                items={projectTypeOptions}
                testId="bulk-edit-content-panel__bulk_edit_project_type"
                containerClassName="col-3 pe-3"
                label="Project Type"
                onSelectionChanged={handleProjectTypeChange}
                selectedItem={projectTypeOptions.find((o) => o.key === projectType)}
              />
            )}
            <MultiselectDropdown
              hideSearch={true}
              items={lineOfBusinessFilterOptions}
              testId="bulk-edit-content-panel__bulk_edit_line_of_business"
              containerClassName="col-3"
              label="Filter Projects by Line of Business"
              onSelectionChanged={(selectedItems) => { lineOfBusinessFilterChange(selectedItems) }}
              selectedItems={selectedItems}
              disabled={lineOfBusinessFilterOptionsDisabled}
            />
            <Waffles<BulkEditModelTable>
              bulkActionBar={
                  <QESButton
                    qesType="secondary"
                    className="p-0 me-4"
                    qesTestId="bulk-project-edit__dashboard-filters-button"
                    onClick={onApplyDashboardFilters}
                    disabled={!canApplyDashboardFilters || isNPMDashboardActive}
                  >
                    Apply Dashboard Filters
                  </QESButton>
              }
              columns={columnConfig}
              data={filteredEditableProjects}
              disableProcessingOverlay={true}
              testId="bulk-projects-edit"
              showCheckboxes={true}
              handleCheckboxChange={handleCheckboxChange}
              selectedKeys={selectedProjects}
              customSort={customSort}
            />
          </>
        }
        {activeEditModule !== ActiveEditModule.Projects &&
          <div>
            {loadingSelectedProjects ? <LoadingSpinner /> : renderBulkEdit()}
          </div>
        }
      </>
    )
  }

  const getDashboardFilters = () => {
    const storedFilters: ResourceFilterSet = JSON.parse(window.localStorage.getItem(`${user.userId}project_filters`) || "{}");
    const combinedFilters = [...(storedFilters.filterGroups || []), ...(storedFilters.pendingChanges || [])];
    return combinedFilters;
  }

  const onApplyDashboardFilters = () => {
    try {
      // intentionally replace any existing selections with these
      handleCheckboxChange(dashboardFilterResourceIds);
      toast("Your Project Dashboard Filters were successfully applied!", {
        autoClose: false,
        containerId: "z-index-1030",
        type: "success",
      });
    } catch {
      toast("Unable to apply Project Dashboard Filters.", {
        autoClose: false,
        containerId: "z-index-1030",
        type: "error",
      });
    }
    setLineOfBusinessFilterOptionsDisabled(true);
  }

  const submitOptions = [
    {
      key: "true",
      name: "Run Now",
    },
    {
      key: "false",
      name: "Run Later",
    },
  ];

  const renderBulkEdit = () => {
    return (
      <>
        {activeEditModule === ActiveEditModule.Template &&
          <>
            <div className="d-flex justify-content-end">
              <QESButton
                qesTestId="bulk-project-edit_reset-template"
                qesType="secondary-danger"
                className="d-flex justify-content-endpull-right mr15"
                disabled={templateResetDisabled}
                onClick={handleTemplateReset}>

                Reset
              </QESButton>
            </div>
            <EditAdequacyTemplateOptions
              shared={false}
              disabled={false}
              useAdequacy={true}
              project={project}
              previousTimeDistanceCalculatorType={TimeDistanceCalculatorType.Unknown}
              templateAccessGranularity={templateAccessGranularity}
              timeDistanceCalculatorTypes={timeDistanceCalculatorTypes}
              projectAccessCodeMapSets={projectAccessCodeMapSets}
              specialtyCodeMapSets={specialtyCodeMapSets}
              sortedSpecialtyGroups={sortedSpecialtyGroups}
              disagreedExcludedSpecialtyGroupCodes={volumeChange?.disagreedExcludedSpecialtyGroupCodes ?? []}
              disagreedTelehealthSpecialtyGroupCodes={volumeChange?.disagreedTelehealthSpecialtyGroupCodes ?? []}
              templateCollections={templateCollections}
              expandSpecialtyMileageCodeMapSets={expandSpecialtyMileageCodeMapSets}
              onTemplateCollectionChange={handleTemplateCollectionChange}
              onTemplateCorrelationIdChange={handleTemplateCorrelationIdChange}
              onTimeDistanceCalculatorTypeChange={handleTimeDistanceCalculatorTypeChange}
              onAccessCodeMapSetChange={handleAccessCodeMapSetChange}
              onExpandSpecialtyMileageChange={handleExpandSpecialtyMileageChange}
              onMarketSpecialtyCodeMapChange={handleMarketSpecialtyCodeMapChange}
              onNetworkSpecialtyCodeMapChange={handleNetworkSpecialtyCodeMapChange}
              onSpecialtyGroupsChange={handleSpecialtyGroupsChange}
              onTelehealthSpecialtyGroupsChange={handleTelelhealthSpecialtyGroupChange}
              user={user}
              isBulkEdit={true}
              adequacyTemplateCollectionIdRef={undefined}
              isZipProject={isZipProject}
              perspective={EditPerspective.Edit}
              showNetworkSpecialtyCodeMap={showNetworkSpecialtyCodeMap}
            />
            {!sortedSpecialtyGroups &&
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <p>Included Specialty Groups are hidden when templates are not the same between selected projects.</p>
                </div>
              </div>}
          </>
        }
        {activeEditModule === ActiveEditModule.Members &&
          <>
            <div className="d-flex justify-content-end">
              <QESButton
                qesTestId="bulk-project-edit_reset-members"
                qesType="secondary-danger"
                className="d-flex justify-content-endpull-right mr15"
                disabled={membersResetDisabled}
                onClick={handleMembersReset}>

                Reset
              </QESButton>
            </div>
            <div className="form-group row tab-contents col-12">
              <div className="col-12 col-sm-9 col-lg-9 member-offset">
                <MemberTable
                  handleMemberPopChange={handleMemberPopChange}
                  handleMemberPopAliasChange={handleMemberPopAliasChange}
                  handleMemberPopChange2={handleMemberPopChange2}
                  handleMemberPopAliasChange2={handleMemberPopAliasChange2}
                  onAlternateMemberPopChange={null}
                  onAlternateMemberPopAliasChange={null}
                  onAddAlternateRow={null}
                  onDeleteAlternateRow={null}
                  handleRiskPopChange={null}
                  disabled={false}
                  generateAlternateMemberships={null}
                  project={project}
                  shared={false}
                  projectMemberPopulations={projectMemberPopulations}
                  history={null}
                  location={null}
                  match={null}
                  allowAlternates={false}
                  isZipProject={isZipProject}
                />
              </div>
            </div>
          </>
        }
        {activeEditModule === ActiveEditModule.Providers &&
          <>
            <div className="d-flex justify-content-end">
              <QESButton
                qesTestId="bulk-project-edit_reset-providers"
                qesType="secondary-danger"
                className="d-flex justify-content-end pull-right mr15"
                disabled={providersResetDisabled}
                onClick={handleProvidersReset}>

                Reset
              </QESButton>
            </div>
            <ProviderOptions
              enabledFeatureFlags={featureFlags}
              checkboxClass={"checkbox"}
              disabled={false}
              hideUseInMarketAnalysis={hideUseInMarketAnalysis}
              marketProviderSets={marketProviderSets}
              onCheckBoxChange={handleProvidersCheckBoxChange}
              onMarketChanged={handleMarketChange}
              onProspectsChanged={handleProspectsChange}
              onProvidersChanged={handleProvidersChange}
              providerSets={providerSets}
              project={project}
              shared={false}
              networkDisabled={networkDisabled}
              hideUseMarketProvidersForImpact={true}
              onChangeMarketAlias={handleMarketProvidersAliasChange}
              onChangeNetworkAlias={handleNetworkProvidersAliasChange}
              onChangeProspectAlias={handleProspectProvidersAliasChange}
              isZipProject={isZipProject}
              onChangeNcpAlias={handleNcpAliasChange}
            />
          </>
        }
        {activeEditModule === ActiveEditModule.NetworkTaxonomy &&
          <>
            <div className="d-flex justify-content-end">
              <QESButton
                qesTestId="bulk-project-edit_reset-network-taxonomy"
                qesType="secondary-danger"
                className="d-flex justify-content-end pull-right mr15"
                disabled={networkTaxonomyResetDisabled}
                onClick={handleNetworkTaxonomyReset}>

                Reset
              </QESButton>
            </div>
            <div className="tab-contents col-12">
              <div className="form-group row">
                <div className="col offset-2 d-inline-block">
                  <NetworkTaxonomyContentPanel
                    networkTaxonomyData={project.networkTaxonomy}
                    onNetworkTaxonomyChange={handleNetworkTaxonomyChange}
                    disabled={false}
                    isBulkEdit={true}
                  />
                </div>
              </div>
            </div>
          </>
        }
        {activeEditModule === ActiveEditModule.Options &&
          <>
            <div className="row">
              <div className="col d-flex justify-content-end">
                <QESButton
                  qesTestId="bulk-project-edit_reset-options"
                  qesType="secondary-danger"
                  className="d-flex justify-content-end pull-right mr15"
                  disabled={optionsResetDisabled}
                  onClick={handleOptionsReset}>

                  Reset
                </QESButton>
              </div>
            </div>
            <EditDisplayOptions
              sidePanelId={undefined}
              accuracyAndAdequacy={false}
              checkboxClass={"checkbox"}
              disabled={false}
              disableUnsupportedOptions={true}
              excludeDeactivatedFacilitiesImpact={volumeChange?.excludeDeactivatedFacilitiesImpact}
              excludeDeactivatedFacilitiesNetwork={volumeChange?.excludeDeactivatedFacilitiesNetwork}
              excludeDeactivatedProvidersImpact={volumeChange?.excludeDeactivatedProvidersImpact}
              excludeDeactivatedProvidersNetwork={volumeChange?.excludeDeactivatedProvidersNetwork}
              excludeInvalidNppesNpiFacilitiesImpact={volumeChange?.excludeInvalidNppesNpiFacilitiesImpact}
              excludeInvalidNppesNpiFacilitiesNetwork={volumeChange?.excludeInvalidNppesNpiFacilitiesNetwork}
              excludeInvalidNppesNpiProvidersImpact={volumeChange?.excludeInvalidNppesNpiProvidersImpact}
              excludeInvalidNppesNpiProvidersNetwork={volumeChange?.excludeInvalidNppesNpiProvidersNetwork}
              excludeMedicareOptOutProvidersImpact={volumeChange?.excludeMedicareOptOutProvidersImpact}
              excludeMedicareOptOutProvidersNetwork={volumeChange?.excludeMedicareOptOutProvidersNetwork}
              excludeMissingNpiFacilitiesImpact={volumeChange?.excludeMissingNpiFacilitiesImpact}
              excludeMissingNpiFacilitiesNetwork={volumeChange?.excludeMissingNpiFacilitiesNetwork}
              excludeMissingNpiProvidersImpact={volumeChange?.excludeMissingNpiProvidersImpact}
              excludeMissingNpiProvidersNetwork={volumeChange?.excludeMissingNpiProvidersNetwork}
              excludeOIGExcludedProvidersImpact={volumeChange?.excludeOIGExcludedProvidersImpact}
              excludeOIGExcludedProvidersNetwork={volumeChange?.excludeOIGExcludedProvidersNetwork}
              excludePrecludedProvidersImpact={volumeChange?.excludePrecludedProvidersImpact}
              excludePrecludedProvidersNetwork={volumeChange?.excludePrecludedProvidersNetwork}
              onCheckBoxChange={handleOptionsCheckBoxChange}
              onImpactExclusionsChange={handleImpactExclusionsChange}
              onNetworkExclusionsChange={handleNetworkExclusionsChange}
              project={project}
              shared={false}
              hideClosestProviderReportOption={false}
              hideCMSApplicationCredit={hideCMSCredit}
              hideCreateStandards={true}
              hideMotiveAppropriatenessScore={hideMotiveAppropriatenessScore}
              hideEnterpriseComplianceDashboard={hideEnterpriseComplianceDashboard}
              hidePracAtLocCondifence={false}
              hideProviderClaimsActivity={false}
              useAccuracy={blendedOnly}
              useAdequacy={true}
              hideStrictTimeDistance={hideStrictTimeDistance}
              ecdCheckboxRef={ecdCheckboxRef}
              isDataShareEligible={isDataShareEligible}
              isZipProject={isZipProject}
              showAffiliationsInsights={volumeChange.showAffiliationsInsights}
              affiliationsDisplayOnNetworkAccordion={volumeChange.affiliationsDisplayOnNetworkAccordion}
            />
          </>
        }
        {activeEditModule === ActiveEditModule.Summary &&
          <div>
            <div className="form-group row">
              <div className="col-12 col-sm-9 col-lg-8 offset-2">
                <AccordionContainer>
                  <Accordion
                    title={<><span>Projects</span><span className="ml15 text-warning">Count: {selectedProjects.length}</span></>}
                    defaultOpen={false}
                    testId="bulk-project-edit__projects-accordion"
                  >
                    <>
                      <ul>
                        <>
                          {projects.map(p =>
                            <li key={p.projectId}>
                              <>
                                <a href={`${window.customUIBaseUrl}/projects/${p.projectId}`} target="_blank" rel="noreferrer">
                                  {p.resource.name}
                                </a>
                                {p.networkTaxonomy?.identifiers?.length > 0 && (volumeInitial?.lineOfBusinessId !== volumeChange?.lineOfBusinessId || volumeInitial?.lineOfBusinessTypeId !== volumeChange?.lineOfBusinessTypeId) ? " (Identifiers will be overwritten)" : ""}
                              </>
                            </li>
                          )}
                        </>
                      </ul>
                    </>
                  </Accordion>
                </AccordionContainer>
              </div>
            </div>
            {volumeChange !== undefined && <>
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <AccordionContainer>
                    <Accordion
                      title={<><span>Template</span><span className="ml15 text-warning">Changes: {templateChangeCount}</span></>}
                      defaultOpen={false}
                      disabled={templateChangeCount === 0}
                      testId="bulk-project-edit__template-accordion"
                    >
                      <>
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.AdequacyTemplateCollectionId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Adequacy Template Collection:</label>
                            <div className="col-8">{getTemplateCollectionDisplayByVolumeChangeId()}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.AdequacyTemplateCorrelationId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Adequacy Template:</label>
                            <div className="col-8">{volumeChange.adequacyTemplateCorrelationId}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.TimeDistanceCalculatorType) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Time/Distance Calculation Type:</label>
                            <div className="col-8">{timeDistanceCalculatorTypeDisplayMap[volumeChange.timeDistanceCalculatorType]}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.AccessCodeMapSetId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">{`${templateHelper.getTemplateGranularityPrefix(templateAccessGranularity)} to Classification Code Map:`}</label>
                            <div className="col-8">{allAccessCodeMapSets.find(c => c.codeMapSetId === volumeChange.accessCodeMapSetId).resource.name}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.NetworkSpecialtyCodeMapSetId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Network Specialty Code Map:</label>
                            <div className="col-8">{specialtyCodeMapSets.find(c => c.codeMapSetId === volumeChange.networkSpecialtyCodeMapSetId)?.resource.name ?? "(Removed)"}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MarketSpecialtyCodeMapSetId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Market Specialty Code Map:</label>
                            <div className="col-8">{specialtyCodeMapSets.find(c => c.codeMapSetId === volumeChange.marketSpecialtyCodeMapSetId)?.resource.name ?? "(Removed)"}</div>
                          </div>}
                        {(volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ExcludedSpecialtyGroupCodes) || volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.IncludedSpecialtyGroupCodes)) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Specialty Group Codes:</label>
                            <div className="col-8">
                              {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.IncludedSpecialtyGroupCodes) && <>
                                <p>The specialty groups below will be added to the existing specialty groups in the selected projects.</p>
                                <ul className="pl30" style={{ listStyleType: "none" }}>
                                  {volumeChange.specialtyGroupCodesToInclude.map(s =>
                                    <li key={s}>
                                      <QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top mr15" color="green" />
                                      <span>{s} - {sortedSpecialtyGroups.find(sg => sg.code === s).name}</span>
                                    </li>
                                  )}
                                </ul>
                              </>}
                              {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ExcludedSpecialtyGroupCodes) && <>
                                <p>The specialty groups below will be removed from the existing specialty groups in the selected projects.</p>
                                <ul className="pl30" style={{ listStyleType: "none" }}>
                                  {volumeChange.specialtyGroupCodesToExclude.map(s =>
                                    <li key={s}>
                                      <QesIcon iconName={QesIcons.Minus} isDecorative className="align-text-top mr15" color="red" />
                                      <span>{s} - {sortedSpecialtyGroups.find(sg => sg.code === s).name}</span>
                                    </li>
                                  )}
                                </ul>
                              </>}
                            </div>
                          </div>}
                        {(volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ExcludedTelehealthSpecailtyGroupCodes) || volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.IncludedTelehealthSpecailtyGroupCodes)) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Telehealth:</label>
                            <div className="col-8">
                              {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.IncludedTelehealthSpecailtyGroupCodes) && <>
                                <p>Telehealth will be added to the specialty groups below in the selected projects.</p>
                                <ul className="pl30" style={{ listStyleType: "none" }}>
                                  {volumeChange.telehealthSpecialtyGroupCodesToInclude.map(s =>
                                    <li key={s}>
                                      <QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top mr15" color="green" />
                                      <span>{s} - {sortedSpecialtyGroups.find(sg => sg.code === s).name}</span>
                                    </li>
                                  )}
                                </ul>
                              </>}
                              {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ExcludedTelehealthSpecailtyGroupCodes) && <>
                                <p>Telehealth will be removed to the specialty groups below in the selected projects.</p>
                                <ul className="pl30" style={{ listStyleType: "none" }}>
                                  {volumeChange.telehealthSpecialtyGroupCodesToExclude.map(s =>
                                    <li key={s}>
                                      <QesIcon iconName={QesIcons.Minus} isDecorative className="align-text-top mr15" color="red" />
                                      <span>{s} - {sortedSpecialtyGroups.find(sg => sg.code === s).name}</span>
                                    </li>
                                  )}
                                </ul>
                              </>}
                            </div>
                          </div>}
                      </>
                    </Accordion>
                  </AccordionContainer>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <AccordionContainer>
                    <Accordion
                      title={<><span>Members</span><span className="ml15 text-warning">Changes: {membersChangeCount}</span></>}
                      defaultOpen={false}
                      disabled={membersChangeCount === 0}
                      testId="bulk-project-edit__members-accordion"
                    >
                      <>
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MemberSetId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Primary Member Population:</label>
                            <div className="col-8">{allMemberPopulations.find(m => m.memberSetId === volumeChange.memberSetId).resource.name}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MemberSetAlias) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Primary Member Population Alias:</label>
                            <div className="col-8">{volumeChange.memberSetAlias !== "" ? volumeChange.memberSetAlias : "(Removed)"}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MemberSetId2) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Secondary Member Population:</label>
                            <div className="col-8">{allMemberPopulations.find(m => m.memberSetId === volumeChange.memberSetId2)?.resource.name ?? "(Removed)"}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MemberSetAlias2) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Secondary Member Population Alias:</label>
                            <div className="col-8">{volumeChange.memberSetAlias2 !== "" ? volumeChange.memberSetAlias2 : "(Removed)"}</div>
                          </div>}
                      </>
                    </Accordion>
                  </AccordionContainer>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <AccordionContainer>
                    <Accordion
                      title={<><span>Providers</span><span className="ml15 text-warning">Changes: {providersChangeCount}</span></>}
                      defaultOpen={false}
                      disabled={providersChangeCount === 0}
                      testId="bulk-project-edit__providers-accordion"
                    >
                      <>
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.NetworkProviderSetId) &&
                          renderProviderSetChange("Network", volumeInitial.networkProviderSetId, volumeChange.networkProviderSetId,
                            providerSets.find((p: ProviderSet) => p.resource.resourceId === volumeChange.networkProviderSetId)?.resource.name)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.NetworkProviderSetAlias) &&
                          renderProviderSetAliasChange("Network", volumeInitial.networkProviderSetAlias, volumeChange.networkProviderSetAlias)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ProspectProviderSetId) &&
                          renderProviderSetChange("Prospect", volumeInitial.prospectProviderSetId, volumeChange.prospectProviderSetId,
                            providerSets.find((p: ProviderSet) => p.resource.resourceId === volumeChange.prospectProviderSetId)?.resource.name)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ProspectProviderSetAlias) &&
                          renderProviderSetAliasChange("Prospect", volumeInitial.prospectProviderSetAlias, volumeChange.prospectProviderSetAlias)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MarketProviderSetId) &&
                          renderProviderSetChange("Market", volumeInitial.marketProviderSetId, volumeChange.marketProviderSetId,
                            marketProviderSets.find((p: ProviderSet) => p.resource.resourceId === volumeChange.marketProviderSetId)?.resource.name)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.MarketProviderSetAlias) &&
                          renderProviderSetAliasChange("Market", volumeInitial.marketProviderSetAlias, volumeChange.marketProviderSetAlias)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.UseNetworkProvidersForMarketAnalysis) &&
                          renderProviderBooleanChange("Network", volumeChange.useNetworkProvidersForMarketAnalysis)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.UseProspectProvidersForMarketAnalysis) &&
                          renderProviderBooleanChange("Prospect", volumeChange.useProspectProvidersForMarketAnalysis)}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.NextClosestProviderSetAlias) &&
                          renderProviderSetAliasChange("Next Closest Network", volumeInitial.nextClosestProviderSetAlias, volumeChange.nextClosestProviderSetAlias)}
                      </>
                    </Accordion>
                  </AccordionContainer>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <AccordionContainer>
                    <Accordion
                      title={<><span>Network Taxonomy</span><span className="ml15 text-warning">Changes: {networkTaxonomyChangeCount}</span></>}
                      defaultOpen={false}
                      disabled={networkTaxonomyChangeCount === 0}
                      testId="bulk-project-edit__network-taxonomy-accordion"
                    >
                      <>
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.FileTypeId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">File Type:</label>
                            <div className="col-8">{volumeChange.fileType}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.FileTypeReason) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Other File Type:</label>
                            <div className="col-8">{volumeChange.fileTypeReason ?? "(Removed)"}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.RegulatorAssignmentClientId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Regulator Assignment:</label>
                            <div className="col-8">{volumeChange.regulatorAssignmentClient}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.ProjectUtilizationCategoryId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Project Utilization Category:</label>
                            <div className="col-8">{volumeChange.projectUtilizationCategory}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.LineOfBusinessId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Line Of Business:</label>
                            <div className="col-8">{volumeChange.lineOfBusiness}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.Reason) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Reason:</label>
                            <div className="col-8">{volumeChange.lineOfBusinessReason ?? "(Removed)"}</div>
                          </div>}
                        {volumeChange.projectChangeReasons?.includes(BulkProjectEditChangeReason.LineOfBusinessTypeId) &&
                          <div className="form-group row">
                            <label className="col-4 demibold">Line Of Business Type:</label>
                            <div className="col-8">{!!volumeChange.lineOfBusinessType ? volumeChange.lineOfBusinessType : "(Removed)"}</div>
                          </div>}
                      </>
                    </Accordion>
                  </AccordionContainer>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <AccordionContainer>
                    <Accordion
                      title={<><span>Options</span><span className="ml15 text-warning">Changes: {optionsChangeCount}</span></>}
                      defaultOpen={false}
                      disabled={optionsChangeCount === 0}
                      testId="bulk-project-edit__options-accordion"
                    >
                      <>
                        {includedOptions.length > 0 && <>
                          <p>The options below will be added to the existing project options in the selected projects.</p>
                          <ul className="pl30" style={{ listStyleType: "none" }}>
                            {includedOptions.map(option =>
                              <li key={option}>
                                <QesIcon iconName={QesIcons.Plus} isDecorative className="align-text-top mr15" color="green" />
                                <span>{option}</span>
                              </li>
                            )}
                          </ul>
                        </>}
                        {excludedOptions.length > 0 && <>
                          <p>The options below will be removed from the existing project options in the selected projects.</p>
                          <ul className="pl30" style={{ listStyleType: "none" }}>
                            {excludedOptions.map(option =>
                              <li key={option}>
                                <QesIcon iconName={QesIcons.Minus} isDecorative className="align-text-top mr15" color="red" />
                                <span>{option}</span>
                              </li>
                            )}
                          </ul>
                        </>}
                      </>
                    </Accordion>
                  </AccordionContainer>
                </div>
              </div>
              <div className="form-group row">
                <div className="col-12 col-sm-9 col-lg-8 offset-2">
                  <div className="d-flex justify-content-end">
                    <NewActionMenu
                      alignRight={true}
                      buttonText="Save Changes"
                      testId="bulk-project-edit__submit-dropdown"
                      buttonClassNames="qes-button-primary"
                      dropup={true}
                      loading={!isValid || (templateChangeCount === 0 && membersChangeCount === 0 && providersChangeCount === 0 && networkTaxonomyChangeCount === 0 && optionsChangeCount === 0) || saveChangesClicked}
                      showSpinner={saveChangesClicked}
                    >
                      {submitOptions.map((option) => {
                        return (
                          <NewActionMenuItem
                            display={option.name}
                            isSelected={false}
                            key={option.key}
                            onClick={() => handleSaveChanges(option.key === "true")}
                            testId={`bulk-project-edit__submit-option--${option.key}`}
                          />
                        );
                      })}
                    </NewActionMenu>
                  </div>
                </div>
              </div>
            </>}
          </div>
        }
      </>
    );
  }

  return (
    <>
      <Page documentTitle="Edit Projects">
        <PageInner>
          <PageHeader title="Edit Projects" disableHR={true}>
          </PageHeader>
        </PageInner>
        <PageSection className="ms-3 me-3">
          {activeEditModule === ActiveEditModule.Projects &&
            <div>
              <p>Select at least one project to continue editing.</p>
              {zipBulkEditEnabled ? (
                !zipProjectTypeSelected &&
                <QESAlert type="warning" persist={true}>Accuracy-only projects are disabled from volume edits at this time.</QESAlert>
              ) : (
                hasAccuracyOnlyOrZipProjects &&
                <QESAlert type="warning" persist={true}>Accuracy-only and Zip Analysis projects are disabled from volume edits at this time.</QESAlert>
              )}
            </div>
          }
          {activeEditModule !== ActiveEditModule.Projects && activeEditModule !== ActiveEditModule.Options &&
            <>
              <p>If any options are changed/updated in this screen, it will update all selected projects. If no changes are made, the options will remain the same on the selected projects.</p>
              {!loading && !loadingSelectedProjects && !isValid &&
                <QESAlert type="danger" persist={true}>
                  The following are required to continue:
                  <ul style={{ marginBottom: 0 }}>
                    {volumeChange.adequacyTemplateCollectionId === 0 && <li>Adequacy Template Collection</li>}
                    {(volumeChange.adequacyTemplateCorrelationId === "" || volumeChange.adequacyTemplateCorrelationId === "0") && <li>Adequacy Template</li>}
                    {volumeChange.accessCodeMapSetId === null && <li>{templateHelper.getTemplateGranularityPrefix(templateAccessGranularity)} to Classification Code Map</li>}
                    {volumeChange.memberSetId === 0 && <li>Primary Membership</li>}
                    {volumeChange.networkProviderSetId === null && <li>Network Provider</li>}
                    {(!volumeChange.fileTypeId || (hasMixedTaxonomy && volumeChange.fileTypeId === -1)) && <li>File Type</li>}
                    {invalidNetworkTaxonomyProperties?.includes(BulkProjectEditChangeReason.FileTypeReason) && <li>Other File Type</li>}
                    {(!volumeChange.projectUtilizationCategoryId || (hasMixedTaxonomy && volumeChange.projectUtilizationCategoryId === -1)) && <li>Project Utilization Category</li>}
                    {(!volumeChange.lineOfBusinessId || (hasMixedTaxonomy && volumeChange.lineOfBusinessId === -1)) && <li>Line Of Business</li>}
                    {invalidNetworkTaxonomyProperties?.includes(BulkProjectEditChangeReason.LineOfBusinessTypeId) && <li>Line Of Business Type</li>}
                    {invalidNetworkTaxonomyProperties?.includes(BulkProjectEditChangeReason.Reason) && <li>Line Of Business Reason</li>}
                    {invalidNetworkTaxonomyProperties?.includes(BulkProjectEditChangeReason.RegulatorAssignmentClientId) && <li>Regulator Assignment</li>}
                  </ul>
                </QESAlert>
              }
              {activeEditModule === ActiveEditModule.Template && volumeChange?.adequacyTemplateCorrelationId !== "-1" && sortedSpecialtyGroups?.length > 0 &&
                <QESAlert type="info" persist={true}>
                  Specialties appearing red, means not all projects have this specialty enabled.
                  <ul style={{ marginBottom: 0 }}>
                    <li>To turn on the specialties in red for all selected projects, click the checkbox once.</li>
                    <li>To turn off the specialties in red for all selected projects, click the checkbox twice.</li>
                    <li>If the specialty remains red, those will not change on selected projects.</li>
                  </ul>
                </QESAlert>
              }
            </>
          }
          {activeEditModule === ActiveEditModule.NetworkTaxonomy && !loadingSelectedProjects &&
            <>
              {projects?.some(p => p.networkTaxonomy?.identifiers?.length > 0) && (volumeInitial?.lineOfBusinessId !== volumeChange?.lineOfBusinessId || volumeInitial?.lineOfBusinessTypeId !== volumeChange?.lineOfBusinessTypeId) &&
                <QESAlert type="warning" persist={true}>
                  Identifiers present in at least one selected project. Changes to Line of Business or Line of Business Type may remove associated identifiers.
                </QESAlert>
              }
              {hasMixedTaxonomy &&
                <QESAlert type="warning" persist={true}>
                  Some selected projects have no network taxonomy information. All fields are required when making edits to network taxonomy.
                </QESAlert>
              }
            </>
          }
          {activeEditModule === ActiveEditModule.Providers && !hideUseInMarketAnalysis && (volumeInitial?.useNetworkProvidersForMarketAnalysis === undefined ||
            volumeInitial?.useProspectProvidersForMarketAnalysis === undefined) &&
            <QESAlert type="info" persist={true}>
              Project Options listed below in red indicate that some, but not all, have this option turned on.
              <ul style={{ marginBottom: 0 }}>
                <li>To turn on the options in red for all selected projects, click the checkbox once.</li>
                <li>To turn off the options in red for all selected projects, click the checkbox twice.</li>
                <li>If the specialty remains red, those will not change on selected projects.</li>
              </ul>
            </QESAlert>
          }
          {activeEditModule === ActiveEditModule.Options &&
            <>
              <p>If any options are changed/updated in this screen, it will update all selected projects. If no changes are made, the options will remain the same on the selected projects.</p>
              <QESAlert type="info" persist={true}>
                Project Options listed below in red indicate that some, but not all, have this option turned on.
                <ul style={{ marginBottom: 0 }}>
                  <li>To turn on the options in red for all selected projects, click the checkbox once.</li>
                  <li>To turn off the options in red for all selected projects, click the checkbox twice.</li>
                  <li>If the specialty remains red, those will not change on selected projects.</li>
                </ul>
              </QESAlert>
            </>}
          {!isProviderSetsValid && activeEditModule !== ActiveEditModule.Projects && !loading && !loadingSelectedProjects &&
            <QESAlert type="danger" persist={true}>
              One or more selected provider sets are invalid and need to be changed.
            </QESAlert>
          }
          <div className="form-horizontal">
            <div className="row">
              <div className="col-12">
                <div className="tabs-style-2 mb-3">
                  <ul className="nav nav-tabs">
                    {renderPane(ActiveEditModule.Projects)}
                    {renderPane(ActiveEditModule.Template)}
                    {renderPane(ActiveEditModule.Members)}
                    {renderPane(ActiveEditModule.Providers)}
                    {renderPane(ActiveEditModule.NetworkTaxonomy)}
                    {renderPane(ActiveEditModule.Options)}
                    {renderPane(ActiveEditModule.Summary)}
                  </ul>
                </div>
              </div>
            </div>
            <div>
              {loading ? <LoadingSpinner /> : renderBulkProjectsEdit()}
            </div>
          </div>
          <Modal
            closeModal={() => setCloseModalWarningVisible(false)}
            content={{
              body: "Are you sure you want to make these changes to all selected projects?",
              cancelButtonText: "Cancel",
              confirmButtonAction: () => handleContinueClick(),
              confirmButtonText: "Continue",
              title: "Warning",
            }}
            isVisible={closeModalWarningVisible}
            hideBodyScroll={true}
          />
          <Modal
            closeModal={() => { setWarningForDisableDescription(""); setWarningForDisableOption(null); }}
            content={{
              body: warningForDisableDescription,
              cancelButtonText: "Cancel",
              confirmButtonAction: () => { toggleOptionsCheckbox(warningForDisableOption); },
              confirmButtonText: "Disable",
              title: "Warning",
            }}
            isVisible={!!warningForDisableDescription && !!warningForDisableOption}
            hideBodyScroll={true}
            triggerElement={ecdCheckboxRef}
          />
        </PageSection>
      </Page>
    </>
  );
};

export default BulkProjectEdit;